import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Delete as DeleteIcon } from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalculateIcon from '@mui/icons-material/Calculate';
import { Controller, useWatch } from 'react-hook-form';
import dayjs from 'dayjs';
import CalSSO from '../../utils/functions/CalSSO';
import CalTax from '../../utils/functions/CalTax';
import api from '../../utils/functions/api';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import EmployeeSalaryReport from '../Card/EmployeeSalaryReport';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EmployeeName from '../EmployeeName';

import SalaryOverrideDialog from './payroll/dialogs/SalaryOverrideDialog';
import AddHoursDialog from './payroll/dialogs/AddHoursDialog';
import BulkDayDialog from './payroll/dialogs/BulkDayDialog';
import AddOtDialog from './payroll/dialogs/AddOtDialog';
import DayDetailView from './payroll/dialogs/DayDetailView';
import OtDetailDialog from './payroll/dialogs/OtDetailDialog';
import NoHoursWarningDialog from './payroll/dialogs/NoHoursWarningDialog';
import {
  NetCell,
  TotalNetCell,
  ActualNetCell,
  TotalActualNetCell,
  SsoIncomeCell,
  CompanyCostCell,
  TotalSsoIncomeCell,
  TotalCompanyCostCell,
  TotalAllowanceCell,
  TotalFieldAllowanceCell,
  TotalBonusCell,
  TotalTaxCell,
  TotalOtherDeductCell,
  CrlDeductCell,
  TotalCrlDeductCell,
  FieldAllowanceBadge,
  ManualDot,
  CarryoverChip,
  sumCarryOut,
} from './payroll/table/cells';
import PayrollTableHeader from './payroll/table/PayrollTableHeader';
import {
  BG,
  RW,
  R,
  LW,
  LEAVE_ABBR,
  LEAVE_COLOR,
  MONTH_NAMES,
  DAY_TH_ABBR,
} from './payroll/constants';

// Virtual scroll constants — defined at module level (never re-created)
const VROW_H = 46;  // estimated row height px (safe for 13px font + padding)
const V_BUF  = 6;   // extra rows rendered above/below viewport
import {
  fmtNum,
  mkInput,
  th0,
  td0,
  weekendCellBg,
  dayTdBase,
  dayCellInner,
  timeToMins,
  calcWorkHours,
  hoursToCheckOutTime,
  calcOtForDay,
  calcCheckinDisplaySalary,
  calcCheckinActualSalary,
} from './payroll/utils';

// ── Module-level helpers (defined once, never re-created per render) ──
const fmt = (n, d = 2) =>
  (n != null
    ? Number(n).toLocaleString('th-TH', { minimumFractionDigits: d, maximumFractionDigits: d })
    : '-');

const bRow = (label, sub, amt, note, noteColor = '#f59e0b') => (
  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
    <div style={{ minWidth: 0 }}>
      <span style={{ color: '#475569', display: 'block', fontSize: '0.72rem' }}>{label}</span>
      {sub && <span style={{ color: '#94a3b8', fontSize: '0.68rem', display: 'block', whiteSpace: 'pre-line' }}>{sub}</span>}
    </div>
    <div style={{ textAlign: 'right', flexShrink: 0 }}>
      <span style={{ fontWeight: 600, fontSize: '0.72rem' }}>{fmt(amt)}</span>
      {note && <span style={{ color: noteColor, fontSize: '0.68rem', display: 'block' }}>{note}</span>}
    </div>
  </div>
);

// ── Lightweight shims: replace MUI Box/Typography/Divider with native HTML ──
function _sp(v) { return typeof v === 'number' ? v * 8 : v; }
function _sx2s(sx) {
  if (!sx) return {};
  const out = {};
  const { mt, mb, ml, mr, pt, pb, pl, pr, px, py, mx, my, m, p, bgcolor, gap, ...rest } = sx;
  if (mt !== undefined) out.marginTop = _sp(mt);
  if (mb !== undefined) out.marginBottom = _sp(mb);
  if (ml !== undefined) out.marginLeft = _sp(ml);
  if (mr !== undefined) out.marginRight = _sp(mr);
  if (pt !== undefined) out.paddingTop = _sp(pt);
  if (pb !== undefined) out.paddingBottom = _sp(pb);
  if (pl !== undefined) out.paddingLeft = _sp(pl);
  if (pr !== undefined) out.paddingRight = _sp(pr);
  if (px !== undefined) { out.paddingLeft = _sp(px); out.paddingRight = _sp(px); }
  if (py !== undefined) { out.paddingTop = _sp(py); out.paddingBottom = _sp(py); }
  if (mx !== undefined) { out.marginLeft = _sp(mx); out.marginRight = _sp(mx); }
  if (my !== undefined) { out.marginTop = _sp(my); out.marginBottom = _sp(my); }
  if (m !== undefined) out.margin = _sp(m);
  if (p !== undefined) out.padding = _sp(p);
  if (bgcolor !== undefined) out.background = bgcolor;
  if (gap !== undefined) out.gap = _sp(gap);
  return { ...out, ...rest };
}
function Box({ sx, style, children, onClick, component, ...rest }) {
  return React.createElement(component || 'div', { style: { ..._sx2s(sx), ...style }, onClick, ...rest }, children);
}
function Typography({ sx, style, variant, component, fontWeight, color, children, onClick, ...rest }) {
  return React.createElement(component || 'span', { style: { fontWeight, color, ..._sx2s(sx), ...style }, onClick, ...rest }, children);
}
// ─────────────────────────────────────────────────────────────────────────────

function MergedPayrollTableV2({
  timestamp,
  month,
  year,
  days,
  selectedOption,
  permission,
  me,
  onSalaryOverride,
  onRefreshTimestamp,
  onSkipLateDeduct,
  manualWorkData = [],
  leaveData = [],
  onManualSave,
  onManualDelete,
  fields = [],
  control,
  getValues,
  setValue,
  remove,
  onOpenCheckinModal,
  typeSelect = 'DAILY',
  attendanceRules = [],
  // default true กันพัง caller เก่าที่ยังไม่ส่ง prop นี้มา (ถือว่าโหลดพร้อมแล้วเหมือนเดิม)
  attendanceRulesLoaded = true,
  onAdjSave,
  onCheckInTimeUpdate,
  onCheckInTimeClear,
  onProjectUpdate,
  onProjectClear,
  onHourOverride,
  onOtOverride,
  isLoading = false,
  dataVersion = 0,
  projectList = [],
  employees = [],
  showFormulas = false,
  salaryMode = 'late-deduct',
  readOnly = false,
  onProjectDashboardChange,
  onEmpSummaryChange,
  // ถ้า parent ดึงมาให้แล้ว (เช่น HrReportV2.js ที่ต้องใช้ holidayDaySet เองสำหรับ PDF export ด้วย)
  // ใช้ตัวนี้แทนการ fetch /holiday ซ้ำอีกรอบ — ถ้าไม่ส่งมา (caller อื่นของตารางนี้) ยัง fetch เอง
  // เหมือนเดิมทุกอย่าง ไม่กระทบพฤติกรรมเดิม
  holidayDaySet: holidayDaySetProp,
  // "new" (ค่าเริ่มต้น) = ต้องมี salary_override.field_allowance_day === true ตรงๆ เท่านั้น
  // "old" = ใช้สูตรเดิมก่อนตัด auto-detect (fallback ไปเดาจากรหัสโครงการถ้ายังไม่มีใครติ๊ก) — ใช้
  // เฉพาะตอนดูงวดเก่าที่ยังไม่บันทึกเงินเดือน (HrReportV2.js ส่ง mode ต่องวดมาให้ ดู
  // fieldAllowanceCalcMode service ฝั่ง backend)
  fieldAllowanceMode = 'new',
  // ยอดยกไปงวดหน้า (PayrollCarryover) — empId → { addTotal, deductTotal, items }
  carryoverByEmpId = {},
  // empId → [auto entries ที่งวดนี้ยกไปงวดหน้า]
  carryoverOutByEmpId = {},
  onOpenCarryover,
}) {
  // ── Virtual scroll state ────────────────────────────────────────────────────
  const [vScrollTop, setVScrollTop] = useState(0);
  const [vContainerH, setVContainerH] = useState(600);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [dlgFieldAllow, setDlgFieldAllow] = useState(false);
  const [forceFullHours, setForceFullHours] = useState(false);
  const [editAmtManual, setEditAmtManual] = useState(null); // null = auto-compute
  const [detailHours, setDetailHours] = useState('');
  const [detailRate, setDetailRate] = useState(1);
  const [saving, setSaving] = useState(false);
  const [skipLateSaving, setSkipLateSaving] = useState(false);
  const [skipLateDvSaving, setSkipLateDvSaving] = useState(false);
  const [editCheckIn, setEditCheckIn] = useState('');
  const [editCheckOut, setEditCheckOut] = useState('');
  const [checkTimeSaving, setCheckTimeSaving] = useState(false);
  const [editProjectId, setEditProjectId] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addTarget, setAddTarget] = useState(null);
  const [addCheckIn, setAddCheckIn] = useState('');
  const [addCheckOut, setAddCheckOut] = useState('');
  const [addAmt, setAddAmt] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [addWorkRate, setAddWorkRate] = useState(1);
  const [addProjectId, setAddProjectId] = useState('');
  const [addIncludeAllowance, setAddIncludeAllowance] = useState(false);
  const [addOtOpen, setAddOtOpen] = useState(false);
  const [addOtTarget, setAddOtTarget] = useState(null);
  const [addOtHours, setAddOtHours] = useState('');
  const [addOtRate, setAddOtRate] = useState(1.5);
  const [addOtSaving, setAddOtSaving] = useState(false);
  const [noHoursWarnDay, setNoHoursWarnDay] = useState(null);
  const [bulkDayOpen, setBulkDayOpen] = useState(false);
  const [bulkDay, setBulkDay] = useState(null);
  const [bulkTab, setBulkTab] = useState(0);
  const [bulkCheckIn, setBulkCheckIn] = useState('08:00');
  const [bulkCheckOut, setBulkCheckOut] = useState('17:00');
  const [bulkRate, setBulkRate] = useState(1);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [calcPopover, setCalcPopover] = useState({
    pos: null,
    title: '',
    content: null,
  });
  const closeCp = () =>
    setCalcPopover({ pos: null, title: '', content: null });
  const [reportEmp, setReportEmp] = useState(null);
  const [fillSaving, setFillSaving] = useState(false);
  const scrollRef = useRef(null);
  // throttle scroll ผ่าน requestAnimationFrame — เดิม setVScrollTop ยิงทุก scroll event (ถี่มาก
  // เวลา trackpad/momentum scroll) ทำให้ re-render รัวเกินความจำเป็น ค่า scrollTop สุดท้ายที่ได้
  // ยังถูกต้องเป๊ะเหมือนเดิม แค่ batch เหลือแค่ 1 ครั้งต่อ frame (ไม่กระทบผลลัพธ์ที่เห็น)
  const pendingScrollTopRef = useRef(null);
  const scrollRafRef = useRef(null);
  const handleScroll = useCallback((e) => {
    pendingScrollTopRef.current = e.currentTarget.scrollTop;
    if (scrollRafRef.current != null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      setVScrollTop(pendingScrollTopRef.current);
    });
  }, []);
  useEffect(() => () => {
    if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current);
  }, []);
  const drag = useRef({
    active: false,
    startX: 0,
    startY: 0,
    sL: 0,
    sT: 0,
    moved: false,
  });
  const adjDebounceRef = useRef({});
  const autoValuesRef = useRef({});
  const dirtyFieldsRef = useRef({});
  const lastRawValueRef = useRef({});
  const [grabbing, setGrabbing] = useState(false);

  // ── Virtual scroll: track container height via ResizeObserver ────────────
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    setVContainerH(el.clientHeight || 600);
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setVContainerH(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []); // eslint-disable-line

  // Reset scroll to top when data or type changes
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setVScrollTop(0);
  }, [dataVersion, typeSelect]); // eslint-disable-line

  // ── Virtual scroll computed range ─────────────────────────────────────────
  const totalEmp = (timestamp?.rows || []).length;
  const rowStart = Math.max(0, Math.floor(vScrollTop / VROW_H) - V_BUF);
  const rowEnd = Math.min(totalEmp, Math.ceil((vScrollTop + vContainerH) / VROW_H) + V_BUF);
  const spacerTop = rowStart * VROW_H;
  const spacerBottom = Math.max(0, (totalEmp - rowEnd) * VROW_H);
  const onScrollDown = (e) => {
    if (e.button !== 0 || e.target.closest('button,input,a,select')) return;
    const el = scrollRef.current;
    if (!el) return;
    drag.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      sL: el.scrollLeft,
      sT: el.scrollTop,
      moved: false,
    };
    setGrabbing(true);
  };
  const onScrollMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
    drag.current.moved = true;
    const el = scrollRef.current;
    if (el) {
      el.scrollLeft = drag.current.sL - dx;
      el.scrollTop = drag.current.sT - dy;
    }
  };
  const onScrollUp = () => {
    drag.current.active = false;
    setGrabbing(false);
  };
  const onScrollClickCapture = (e) => {
    if (drag.current.moved) {
      e.stopPropagation();
      drag.current.moved = false;
    }
  };
  const [dvOpen, setDvOpen] = useState(false);
  const [dvData, setDvData] = useState(null);
  const [otDvOpen, setOtDvOpen] = useState(false);
  const [otDvData, setOtDvData] = useState(null);
  const openOtDv = (e, otReq, manualEntry, empName, checkin, day) => {
    e.preventDefault();
    setOtDvData({ otReq, manualEntry, empName, checkin, day });
    setOtDvOpen(true);
  };
  const openDv = (
    e,
    checkin,
    empName,
    manualEntry,
    spd,
    leave,
    leaveAutoH,
    dayNum,
  ) => {
    e.preventDefault();
    const autoAmt =
      checkin?.totalTime != null
        ? Number((checkin.totalTime * (spd || 0)).toFixed(2))
        : checkin?.totalHour != null
        ? Number(((checkin.totalHour / 8) * (spd || 0)).toFixed(2))
        : null;
    setDvData({
      checkin,
      empName,
      manualEntry,
      autoAmt,
      leave,
      leaveAutoH: leaveAutoH || 0,
      dayNum,
    });
    setDvOpen(true);
  };

  const fillSundays = async () => {
    if (readOnly || !onManualSave || !timestamp?.rows?.length) return;
    const sundays = days.filter(
      (d) => dayjs(`${year}-${month + 1}-${d}`).day() === 0,
    );
    if (!sundays.length) return;
    setFillSaving(true);
    try {
      const tasks = [];
      timestamp.rows.forEach((emp) => {
        const empId = String(emp.employee_id);
        sundays.forEach((d) => {
          const hasCheckin = emp.checkins?.some(
            (c) =>
              dayjs(c?.checkIn).format('D/M/YY') ===
              `${d}/${month + 1}/${year % 100}`,
          );
          const hasMn = getManualHours(empId, d) > 0;
          if (!hasCheckin && !hasMn) {
            const mm = String(month + 1).padStart(2, '0');
            const dd = String(d).padStart(2, '0');
            tasks.push(
              onManualSave({
                employee_id: empId,
                employee_name: emp.name,
                date: `${year}-${mm}-${dd}T00:00:00.000Z`,
                hours: 8,
                salary_per_day: emp.salary_per_day,
                amount: emp.salary_per_day || 0,
                edited_by: me?.firstname ? formatEmployeeThaiName(me) : 'ไม่ระบุ',
              }),
            );
          }
        });
      });
      await Promise.all(tasks);
    } finally {
      setFillSaving(false);
    }
  };

  const editorName = me?.firstname ? formatEmployeeThaiName(me) : 'ไม่ระบุ';
  const hrMgmt = permission?.hr_management;
  const today = dayjs();
  const isCurrentMonth = today.year() === year && today.month() === month;
  const todayDay = today.date();

  const fieldIndexMap = useMemo(() => {
    const m = {};
    fields.forEach((f, i) => {
      m[String(f.employee_id)] = i;
    });
    return m;
  }, [fields]);

  // ใช้ดึงยอด "เงินได้อื่นๆ" สดจากฟอร์ม (รวม override ที่ยังไม่ได้ save) สำหรับ projectDashboard
  const watchedSalaryData = useWatch({ control, name: 'salaryData' }) || [];

  // O(1) lookup: key = `${empId}-${dayOfMonth}`
  const manualByKey = useMemo(() => {
    const map = {};
    manualWorkData.forEach((e) => {
      const d = new Date(e.date);
      if (d.getUTCFullYear() !== year || d.getUTCMonth() + 1 !== month + 1)
        return;
      map[`${e.employee_id}-${d.getUTCDate()}`] = e;
    });
    return map;
  }, [manualWorkData, year, month]);

  // O(1) lookup: checkin ต่อ empId+day + checkin ที่มี OT ผูกอยู่จริงต่อ empId+day
  // สร้างสองตัวในลูปเดียวกัน (เดิมแยกเป็น 2 useMemo ที่วน emp.checkins ซ้ำกัน 2 รอบ)
  // - checkinByKey: ใช้โชว์ชั่วโมง/ค่าแรง — วันที่มี checkin ซ้ำ (จริง+HR เพิ่ม manual ทับ) ให้ checkin
  //   จริง (is_manual=false) ชนะเสมอ ไม่งั้นลำดับ array จาก Mongo aggregate (ไม่ได้ sort) จะสุ่มว่าใครทับใคร
  // - otCheckinByKey: ใช้หา OT โดยเฉพาะ แยกจาก checkinByKey เพราะ OT อาจผูกอยู่กับ checkin ที่ไม่ถูกเลือก
  //   มาโชว์ชั่วโมงก็ได้ (เช่น ผูกกับตัวจริง แต่ checkinByKey ดันเลือกตัว manual) เลยต้องหา "ตัวที่มี OT" แยกต่างหาก
  const { checkinByKey, otCheckinByKey } = useMemo(() => {
    const map = {};
    const otMap = {};
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      (emp.checkins || []).forEach((c) => {
        if (!c?.checkIn) return;
        const d = dayjs(c.checkIn);
        if (d.year() !== year || d.month() !== month) return;
        const key = `${empId}-${d.date()}`;

        const existing = map[key];
        if (!(existing && !existing.is_manual && c.is_manual)) map[key] = c;

        const hasOt = Array.isArray(c.otRequests)
          ? c.otRequests.length > 0
          : !!c.otRequests?.total_hours;
        if (hasOt || !otMap[key]) otMap[key] = c;
      });
    });
    return { checkinByKey: map, otCheckinByKey: otMap };
  }, [timestamp, year, month]);

  // OT รวม+รายวันต่อพนักงาน คำนวณครั้งเดียวที่นี่ (single source of truth) — column, popup "สูตร OT",
  // และ EmployeeSalaryReport ดึงยอดรวมจากจุดนี้ผ่าน otTotalMap (ตัว breakdown รายวันใช้ตรงใน buildOtContent)
  // ไม่ใช้ emp.totalOtAmount (backend aggregate) ตรงๆ เพราะไม่สะท้อนการแก้ไข/เพิ่ม OT แบบ real-time
  const otBreakdownMap = useMemo(() => {
    const map = {};
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const hrR = (emp.salary_per_day || 0) / 8;
      const getDayHrRate = (ck) => {
        if (!ck) return hrR;
        if (ck.salary_override?.amount != null) return ck.salary_override.amount / 8;
        const er2 = ck.effectiveRate || ck.rate || 1;
        return (er2 * (emp.salary_per_day || 0)) / 8;
      };
      let total = 0;
      const rows = [];
      const byDay = {};
      days.forEach((d2) => {
        const ck = otCheckinByKey[`${empId}-${d2}`];
        const manE = manualByKey[`${empId}-${d2}`];
        const dayHrR = getDayHrRate(ck);
        const ot = calcOtForDay(ck, manE, dayHrR);
        byDay[d2] = { checkin: ck, dayHrR, ...ot };
        if (ot.otHours <= 0) return;
        total += ot.otAmt;
        rows.push({ day: d2, checkin: ck, dayHrR, ...ot });
      });
      map[empId] = { total: Number(total.toFixed(2)), rows, byDay };
    });
    return map;
  }, [timestamp, manualByKey, otCheckinByKey, days]);

  const otTotalMap = useMemo(() => {
    const map = {};
    Object.keys(otBreakdownMap).forEach((empId) => {
      map[empId] = otBreakdownMap[empId].total;
    });
    return map;
  }, [otBreakdownMap]);

  const employeeMap = useMemo(() => {
    const m = {};
    employees.forEach((e) => {
      m[String(e._id || e.id)] = e;
    });
    return m;
  }, [employees]);

  // ค่าออกสนามรวม+รายวันต่อพนักงาน คำนวณครั้งเดียวที่นี่ (single source of truth เหมือน otBreakdownMap)
  // — column, popup "สูตรค่าออกสนาม", และ buildDailyRowsForReport ดึงจากจุดนี้ทั้งหมด
  const faBreakdownMap = useMemo(() => {
    const globalRate =
      timestamp?.company_setting?.payroll?.field_allowance_rate || 200;
    const map = {};
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const empFA =
        emp.field_allowance || employeeMap[empId]?.field_allowance || null;
      const faEnabled = !!empFA?.enabled;
      const faDay =
        empFA?.override_amount != null ? empFA.override_amount : globalRate;
      const faHrRate = faDay / 8;
      let total = 0;
      const rows = [];
      const byDay = {};
      // ต้องเช็ค faEnabled ด้วย ไม่ใช่แค่ faDay (faDay fallback เป็น globalRate เสมอแม้พนักงานไม่มีสิทธิ์
      // รับค่าออกสนามเลย ทำให้คนที่ไม่มีป้าย "รับค่าออกสนาม" ได้ยอดคำนวณไปด้วยถ้าลงเวลาโปรเจค JS)
      if (faEnabled && faDay) {
        days.forEach((d) => {
          const ck = checkinByKey[`${empId}-${d}`];
          if (!ck) return;
          const savedFA = ck.salary_override?.field_allowance_day;
          let isFA = savedFA === true;
          if (fieldAllowanceMode === 'old' && savedFA == null) {
            const pn = (ck.project_number || '').toLowerCase().trim();
            isFA = pn.startsWith('js') && !pn.startsWith('jsid');
          }
          if (!isFA) return;
          const actualH = ck.hour_override?.hours ?? ck.totalHour ?? 0;
          const faHours = Math.min(actualH > 0 ? actualH : 8, 8);
          let amt = Number((faHours * faHrRate).toFixed(2));
          const ot = otBreakdownMap[empId]?.byDay?.[d];
          const otHours = ot?.otHours || 0;
          const otRate = ot?.otRate || 1.5;
          if (otHours > 0) amt += Number((otHours * otRate * faHrRate).toFixed(2));
          total += amt;
          const row = { day: d, checkin: ck, faHours, faHrRate, amt, otHours, otRate };
          rows.push(row);
          byDay[d] = row;
        });
      }
      map[empId] = {
        total: Number(total.toFixed(2)),
        rows,
        byDay,
        faDay,
        faHrRate,
        faEnabled,
      };
    });
    return map;
  }, [timestamp, employeeMap, checkinByKey, days, otBreakdownMap, fieldAllowanceMode]);

  // O(1) lookup: leave ต่อ empId+day (expand range ออกเป็น day keys)
  const leaveByKey = useMemo(() => {
    const map = {};
    const dim = dayjs(`${year}-${month + 1}-01`).daysInMonth();
    (leaveData || []).forEach((lr) => {
      const empId = String(lr.employee?._id || lr.employee);
      const s = dayjs(lr.start_date).startOf('day');
      const e = dayjs(lr.end_date).endOf('day');
      for (let d = 1; d <= dim; d++) {
        const t = dayjs(
          `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(
            2,
            '0',
          )}`,
        );
        if (!t.isBefore(s) && !t.isAfter(e)) map[`${empId}-${d}`] = lr;
      }
    });
    return map;
  }, [leaveData, year, month]);

  const getEntry = (empId, day) =>
    manualByKey[`${String(empId)}-${day}`] || null;
  const getManualHours = (empId, day) =>
    manualByKey[`${String(empId)}-${day}`]?.hours || 0;
  const getManualOtHours = (empId, day) =>
    manualByKey[`${String(empId)}-${day}`]?.ot_hours || 0;

  // วันหยุดของเดือนที่กำลังแสดง — ใช้ระบายสี column วันที่ตรงกับวันหยุด (management/holiday) เหมือนวันเสาร์
  // วันหยุดใช้ซ้ำทุกปี (ไม่ผูกกับปีที่บันทึกไว้) — เทียบแค่เดือนตรงกันไหม ไม่สนใจปี
  const [internalHolidayDaySet, setInternalHolidayDaySet] = useState(() => new Set());
  useEffect(() => {
    if (holidayDaySetProp) return; // parent ส่ง holidayDaySet มาให้แล้ว ไม่ต้อง fetch เอง
    let cancelled = false;
    api
      .get(`${process.env.REACT_APP_API_URL}/holiday?size=1000&page=1`)
      .then(({ data }) => {
        if (cancelled) return;
        const set = new Set(
          (data?.rows || [])
            .filter((h) => dayjs(h.date).month() === month)
            .map((h) => dayjs(h.date).date()),
        );
        setInternalHolidayDaySet(set);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [month, holidayDaySetProp]);
  const holidayDaySet = holidayDaySetProp || internalHolidayDaySet;

  // Pre-compute day-of-week per day once — shared by header + every body row (replaces O(emp×day) dayjs calls)
  const dayMeta = useMemo(() => {
    return days.map((day) => {
      const dow = new Date(year, month, day).getDay();
      return {
        day,
        dow,
        isWeekend: dow === 0 || dow === 6,
        isToday: isCurrentMonth && day === todayDay,
        isHoliday: holidayDaySet.has(day),
      };
    });
  }, [days, year, month, isCurrentMonth, todayDay, holidayDaySet]);

  // sync ค่า OT column ใน form ทุกครั้งที่ manualWorkData เปลี่ยน — ใช้ otTotalMap (ตรงกับ popup สูตร OT)
  useEffect(() => {
    if (!setValue || !fields.length) return;
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const fi = fieldIndexMap[empId];
      if (fi === undefined) return;
      const total = otTotalMap[empId] ?? 0;
      setValue(
        `salaryData.${fi}.revenue.overtime`,
        total.toFixed(2),
      );
    });
  }, [otTotalMap, fields]); // eslint-disable-line

  // sync ค่า หักสาย ทุกครั้งที่ data หรือ rules เปลี่ยน — คำนวณเหมือน calcDayLateDeduction ใน EmployeeSalaryReport
  // ต้องรอ attendanceRulesLoaded ก่อน (ไม่ใช่แค่เช็ค attendanceRules.length) — ไม่งั้นตอนหน้าเว็บเพิ่งโหลด
  // (ก่อน getInformation() เสร็จ) attendanceRules จะเป็น [] ชั่วคราว ทำให้ lRule หาไม่เจอ คิดหักสายแบบ
  // "ไม่มี threshold ผ่อนผัน" (คิดทุกนาทีที่สาย) ซึ่งผิด แล้วพอโหลดกฎจริงเสร็จค่อยคำนวณใหม่เป็นค่าถูก —
  // ผู้ใช้เห็นยอดในตาราง/TimestampProjectCost เปลี่ยนเองทั้งที่ไม่ได้แก้อะไรเลย
  useEffect(() => {
    if (!setValue || !fields.length || !attendanceRulesLoaded) return;
    const lRule = (attendanceRules || []).find(
      (r) => r.type === 'LATE_ARRIVAL' && r.enabled,
    );
    const WS = 8 * 60;
    (timestamp?.rows || []).forEach((emp) => {
      const fi = fieldIndexMap[String(emp.employee_id)];
      if (fi === undefined) return;
      const spm = emp.salary_per_month || 0;
      const spd = emp.salary_per_day || 0;
      let lateTotal = 0;
      const uniqueCk = [];
      const seen = new Set();
      [...(emp.checkins || [])].reverse().forEach((ck) => {
        const dk = dayjs(ck.checkIn).format('YYYY-MM-DD');
        if (!seen.has(dk)) {
          seen.add(dk);
          uniqueCk.unshift(ck);
        }
      });
      uniqueCk.forEach((ck) => {
        if (ck.is_manual || ck.skip_late_deduct?.enabled) return;
        if (ck.hour_override?.hours != null) return;
        const cit = ck.checkInTime;
        if (!cit) return;
        const [citH, citM] = cit.split(':').map(Number);
        const shiftStart = citH >= 12 ? 13 * 60 : WS;
        const lateMin = Math.max(0, citH * 60 + citM - shiftStart);
        if (!lateMin) return;
        let effMin;
        if (!lRule) {
          effMin = lateMin;
        } else {
          if (lateMin < lRule.threshold_minutes) return;
          effMin = Math.max(lateMin, lRule.deduct_minutes || 0);
        }
        const baseDr =
          ck.salary_override?.day_rate ?? (spm > 0 ? spm / 30 : spd);
        const ckRate =
          ck.salary_override?.rate ?? ck.effectiveRate ?? ck.rate ?? 1;
        const ckMinRate = (baseDr * ckRate) / 8 / 60;
        lateTotal += Number((effMin * ckMinRate).toFixed(2));
      });
      setValue(
        `salaryData.${fi}.expenses.late`,
        lateTotal.toFixed(2),
      );
    });
  }, [timestamp, attendanceRules, fields, attendanceRulesLoaded]); // eslint-disable-line

  // DAILY: backend รวม manual ใน totalAmount ผ่าน totalWork → ข้าม timestamp_ref เพื่อกัน double-count
  // FULLTIME: backend ตั้ง checkinAmount=0 สำหรับ manual entry → ต้องนับที่นี่เสมอ
  const getManualAmt = (empId, spd, empType) =>
    days.reduce((s, d) => {
      const e = getEntry(empId, d);
      if (!e) return s;
      if (empType !== 'FULLTIME' && e.timestamp_ref) return s;
      return s + (e.amount != null ? e.amount : (e.hours / 8) * (spd || 0));
    }, 0);

  // O(1) lookup แทน O(n) array.find()
  const getLeaveForDay = (empId, day) =>
    leaveByKey[`${String(empId)}-${day}`] || null;

  // Pre-compute per-employee heavy values ทั้งหมดใน 1 pass (แทนการคำนวณซ้ำใน JSX loop)
  const empSummaryMap = useMemo(() => {
    const result = {};
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const spd = emp.salary_per_day || 0;
      const isFulltime = emp.emp_type === 'FULLTIME';

      // workingDays + leaveHalfDays
      const workingDays = [];
      let leaveHalfDays = 0;
      days.forEach((d) => {
        const lv = leaveByKey[`${empId}-${d}`];
        const ck = checkinByKey[`${empId}-${d}`];
        const entry = manualByKey[`${empId}-${d}`];
        if (lv && lv.period === 'FULL_DAY') {
          if (isFulltime) workingDays.push(d);
        } else {
          const mh = entry?.hours || 0;
          if (ck || (mh > 0 && !entry?.timestamp_ref)) workingDays.push(d);
          if (isFulltime && lv && lv.period !== 'FULL_DAY' && !ck)
            leaveHalfDays += 0.5;
        }
      });

      // displayCheckEff = sum of rate per working day + leaveHalfDays
      const displayCheckEff =
        workingDays.reduce((sum, d) => {
          const ck = checkinByKey[`${empId}-${d}`];
          return (
            sum +
            (ck ? (ck.is_manual ? 1 : ck.effectiveRate || ck.rate || 1) : 1)
          );
        }, 0) + leaveHalfDays;

      // leave salary contribution (FULLTIME เท่านั้น)
      // full-day = 8h credit, half-day = 4h credit (checkin จะบวกเพิ่มอีกไม่เกิน 4h)
      let leaveSalaryAmt = 0;
      if (isFulltime) {
        days.forEach((d) => {
          const lv = leaveByKey[`${empId}-${d}`];
          if (!lv) return;
          leaveSalaryAmt += lv.period === 'FULL_DAY' ? spd : spd / 2;
        });
      }

      // deduplicate checkins per day (last checkin per date wins) — ตรงกับ popup
      const seenCkDates = new Set();
      const uniqueCheckins = [];
      for (const ck of [...(emp.checkins || [])].reverse()) {
        const dk = dayjs(ck.checkIn).format('YYYY-MM-DD');
        if (!seenCkDates.has(dk)) {
          seenCkDates.add(dk);
          uniqueCheckins.unshift(ck);
        }
      }

      // actual-hours salary — calcCheckinActualSalary (single source of truth, ตรงกับ popup + modal)
      const actualCheckinSalary = uniqueCheckins.reduce((s, ck) => {
        const ckDay = dayjs(ck.checkIn).date();
        const lv = isFulltime ? leaveByKey[`${empId}-${ckDay}`] : null;
        const r = calcCheckinActualSalary(ck, lv, spd);
        return s + (r?.amt || 0);
      }, 0);

      // checkin salary (displayAmt) — calcCheckinDisplaySalary (single source of truth, ตรงกับ popup + modal)
      const checkinSalary = uniqueCheckins.reduce((s, ck) => {
        const ckDay = dayjs(ck.checkIn).date();
        const lv = isFulltime ? leaveByKey[`${empId}-${ckDay}`] : null;
        const r = calcCheckinDisplaySalary(ck, lv, spd);
        return s + (r?.amt || 0);
      }, 0);

      // manual salary amount
      const manualAmt = days.reduce((s, d) => {
        const entry = manualByKey[`${empId}-${d}`];
        if (!entry) return s;
        if (!isFulltime && entry.timestamp_ref) return s;
        return (
          s + (entry.amount != null ? entry.amount : (entry.hours / 8) * spd)
        );
      }, 0);

      // net OT — derive จาก otBreakdownMap ตรงๆ (single source of truth เดียวกับ popup "สูตร OT"
      // และ EmployeeSalaryReport) แทนการ reconcile totalOtHour ฐานกับ manual เอง ซึ่งเพี้ยนง่าย
      // เวลาวันเดียวกันมีหลาย checkin/OT request ผูกอยู่ (ต้นเหตุตัวเลขคอลัมน์สรุปไม่ตรงกับ popup)
      const otRows = otBreakdownMap[empId]?.rows || [];
      const netOtH = otRows.reduce((s, r) => s + (r.otHours || 0), 0);
      const netOtEffH = otRows.reduce(
        (s, r) => s + (r.otHours || 0) * (r.otRate || 1.5),
        0,
      );

      const _displayAmt = checkinSalary + leaveSalaryAmt + manualAmt;
      // FULLTIME (พนักงานรายเดือน): ค่าแรง (ชม.จริง) = Day Rate ทุกวันเป็นฐาน (spd × 30) เท่านั้น
      // ไม่ขึ้นกับ checkin/ลา/manual/rate พิเศษวันหยุด — ได้เงินเดือนเต็มเสมอ
      const _actualSalaryAmt = isFulltime
        ? spd * 30
        : actualCheckinSalary + leaveSalaryAmt + manualAmt;
      result[empId] = {
        workingDays,
        leaveHalfDays,
        displayCheck: workingDays.length + leaveHalfDays,
        displayCheckEff,
        manualAmt,
        displayAmt: Math.round(_displayAmt * 100) / 100,
        actualSalaryAmt: Math.round(_actualSalaryAmt * 100) / 100,
        netOtH,
        netOtEffH,
      };
    });
    return result;
  }, [timestamp, days, manualByKey, checkinByKey, otBreakdownMap, leaveByKey]); // eslint-disable-line

  // ── projectDashboard: กลุ่มค่าแรง/OT/ค่าออกสนาม ตามโครงการ (DAILY: ตาม checkin จริงรายวัน,
  //    FULLTIME: ค่าแรงเต็มเดือน/OT/เบี้ยเลี้ยง ลงโครงการหลัก แต่ "ค่าออกสนาม" แยกตามโครงการที่ลงเวลาจริงรายวัน) ──
  // ใช้สูตรเดียวกับ empSummaryMap เป๊ะๆ (calcCheckinActualSalary + otBreakdownMap + faBreakdownMap)
  // แล้วรายงานขึ้น parent ผ่าน onProjectDashboardChange เพื่อให้ TimestampProjectCost ใช้เลขชุดเดียวกัน
  // ไม่ต้องพึ่ง loop สรุปรายโปรเจคฝั่ง backend ที่คำนวณแยกกันคนละสูตร (เป็นต้นเหตุที่ยอดสองตารางไม่ตรงกัน)
  const projectDashboard = useMemo(() => {
    const byProject = {};
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const spd = emp.salary_per_day || 0;
      const isFulltime = emp.emp_type === 'FULLTIME';

      const seenCkDates = new Set();
      const uniqueCk = [];
      for (const ck of [...(emp.checkins || [])].reverse()) {
        const dk = dayjs(ck.checkIn).format('YYYY-MM-DD');
        if (!seenCkDates.has(dk)) {
          seenCkDates.add(dk);
          uniqueCk.unshift(ck);
        }
      }

      // FULLTIME: ค่าแรงเต็มเดือน/OT/เบี้ยเลี้ยง ลงเป็นก้อนเดียวที่ "โครงการหลัก" (ชั่วโมงเช็คอินสะสมมากสุด)
      // เพราะเงินเดือนเต็มเดือนไม่ผูกกับ checkin ไหนโดยเฉพาะ. **ยกเว้น "ค่าออกสนาม" (FA)** — user ต้องการให้ดูจาก
      // daycell จริงว่าลงวันไหนที่โครงการไหน แล้วลงโครงการนั้นตรง ๆ (ดู fulltimeFaByProject ด้านล่าง) total_payable
      // ต่อโครงการก็ย้าย FA ส่วนนั้นตามไปด้วยเพื่อให้แต่ละแถว reconcile
      // "เงินได้อื่นๆ" (bonus) ก็เป็นยอดต่อคน/งวด ไม่ผูกกับ checkin เหมือนกัน (ทุกประเภทพนักงาน ไม่ใช่แค่ FULLTIME)
      // เลยต้องนับชั่วโมงสะสมต่อโครงการไว้ก่อน เพื่อหา "โครงการหลัก" แล้วค่อยลงยอดเป็นก้อนเดียวทีหลัง
      const empHoursByProject = {};
      const empProjectInfo = {};
      // เก็บสัดส่วนรายได้ต่อโครงการของพนักงานคนนี้ (เฉพาะ DAILY) ไว้กระจาย total_payable ตามสัดส่วนจริงทีหลัง
      // แทนที่จะยัดทั้งก้อนไปที่ "โครงการหลัก" เดียว (ต้นเหตุโครงการที่ไม่ใช่โครงการหลักโชว์ "ที่ต้องจ่าย" = 0 ทั้งที่มีค่าแรง/OT อยู่)
      const empProjectGross = {};
      let fulltimeOtTotal = 0;
      let fulltimeAllowanceTotal = 0;
      // ค่าออกสนาม (FA) ของพนักงานรายเดือน แยกตามโครงการที่ลงเวลาจริงในแต่ละวัน (ไม่ยัดก้อนเดียวลงโครงการหลัก)
      const fulltimeFaByProject = {};

      uniqueCk.forEach((ck) => {
        const ckDay = dayjs(ck.checkIn).date();
        const projectId = ck.project_id ? String(ck.project_id) : 'no_project';
        const hours = ck.hour_override?.hours ?? ck.totalHour ?? 0;
        empHoursByProject[projectId] = (empHoursByProject[projectId] || 0) + hours;
        if (!empProjectInfo[projectId]) {
          empProjectInfo[projectId] = {
            project_id: ck.project_id || null,
            project_number: ck.project_number || '',
            project_name: ck.project_name || '',
          };
        }

        const otAmt = otBreakdownMap[empId]?.byDay?.[ckDay]?.otAmt || 0;
        const faAmt = faBreakdownMap[empId]?.byDay?.[ckDay]?.amt || 0;
        const allowanceAmt = (ck.extra_day || 0) + (ck.extra_month || 0);

        if (isFulltime) {
          fulltimeOtTotal += otAmt;
          fulltimeAllowanceTotal += allowanceAmt;
          // FA ลงโครงการที่ลงเวลาจริงวันนั้น (ดูจาก daycell) — OT/เบี้ยเลี้ยง/ค่าแรงเต็มเดือนยังลงโครงการหลักตามเดิม
          if (faAmt > 0) {
            fulltimeFaByProject[projectId] = (fulltimeFaByProject[projectId] || 0) + faAmt;
          }
          return;
        }

        const salaryAmt = calcCheckinActualSalary(ck, null, spd)?.amt || 0;
        if (!byProject[projectId]) {
          byProject[projectId] = {
            project_id: ck.project_id || null,
            project_number: ck.project_number || '',
            project_name: ck.project_name || '',
            employees: [],
            // เงินได้อื่นๆ/หัก ต่อ "คน" ต่อโครงการนี้ (ไม่ใช่ต่อวันเหมือน employees[]) — ใช้ทำใบสรุปค่าแรงต่อโครงการ
            // (laborProjectReceiptPDF.js) กระจายตามสัดส่วนเดียวกับ total_payable (ดู DAILY/FULLTIME ด้านล่าง)
            employee_deductions: [],
            total_salary: 0,
            total_ot: 0,
            total_field_allowance: 0,
            total_allowance: 0,
            total_bonus: 0,
            total_crl: 0,
            total_payable: 0,
          };
        }
        byProject[projectId].employees.push({
          employee_id: emp.employee_id,
          name: emp.name,
          amount: salaryAmt,
          ot_amount: otAmt,
          field_allowance_amount: faAmt,
          allowance: allowanceAmt,
          salary_per_day: spd,
          // TimestampProjectCost.js คำนวณ amount ใหม่จาก rate*8*(salary_per_day/8) เพื่อรองรับ override รายคน
          // ต้อง back-solve rate ให้คูณกลับได้ salaryAmt เป๊ะๆ ไม่งั้นค่าแรง/OT จะกลายเป็น 0 (salary_per_day/rate ไม่มีค่า)
          rate: spd > 0 ? salaryAmt / spd : 0,
        });
        byProject[projectId].total_salary += salaryAmt;
        byProject[projectId].total_ot += otAmt;
        byProject[projectId].total_field_allowance += faAmt;
        byProject[projectId].total_allowance += allowanceAmt;
        empProjectGross[projectId] = (empProjectGross[projectId] || 0) + salaryAmt + otAmt + faAmt + allowanceAmt;
      });

      // โครงการหลัก = ชั่วโมงสะสมมากที่สุดในงวดนี้ — ถ้าไม่มี checkin เลยทั้งงวด ตกไป "no_project"
      // ใช้ร่วมกันทั้งค่าแรงเต็มเดือน (FULLTIME) และ "เงินได้อื่นๆ" (ทุกประเภทพนักงาน)
      let dominantProjectId = 'no_project';
      let maxHours = -1;
      Object.entries(empHoursByProject).forEach(([pid, h]) => {
        if (h > maxHours) {
          maxHours = h;
          dominantProjectId = pid;
        }
      });
      const ensureDominantBucket = () => {
        if (!byProject[dominantProjectId]) {
          const info = empProjectInfo[dominantProjectId] || {};
          byProject[dominantProjectId] = {
            project_id: info.project_id || null,
            project_number: info.project_number || '',
            project_name: info.project_name || '',
            employees: [],
            // เงินได้อื่นๆ/หัก ต่อ "คน" ต่อโครงการนี้ (ไม่ใช่ต่อวันเหมือน employees[]) — ใช้ทำใบสรุปค่าแรงต่อโครงการ
            // (laborProjectReceiptPDF.js) กระจายตามสัดส่วนเดียวกับ total_payable (ดู DAILY/FULLTIME ด้านล่าง)
            employee_deductions: [],
            total_salary: 0,
            total_ot: 0,
            total_field_allowance: 0,
            total_allowance: 0,
            total_bonus: 0,
            total_crl: 0,
            total_payable: 0,
          };
        }
      };

      if (isFulltime) {
        ensureDominantBucket();
        const fullWage = spd * 30;
        const faDominant = fulltimeFaByProject[dominantProjectId] || 0;
        byProject[dominantProjectId].employees.push({
          employee_id: emp.employee_id,
          name: emp.name,
          amount: fullWage,
          ot_amount: fulltimeOtTotal,
          field_allowance_amount: faDominant, // เฉพาะ FA ของวันที่ลงโครงการหลักจริง
          allowance: fulltimeAllowanceTotal,
          salary_per_day: spd,
          rate: spd > 0 ? fullWage / spd : 0,
        });
        byProject[dominantProjectId].total_salary += fullWage;
        byProject[dominantProjectId].total_ot += fulltimeOtTotal;
        byProject[dominantProjectId].total_field_allowance += faDominant;
        byProject[dominantProjectId].total_allowance += fulltimeAllowanceTotal;

        // FA ของวันที่ลงโครงการอื่น — ลงโครงการนั้นตรง ๆ (สร้าง bucket ถ้ายังไม่มี) + push employees entry
        // แบบมีแค่ FA (amount/ot = 0) เพื่อให้ TimestampProjectCost คำนวณ total_field_allowance ต่อโครงการถูก
        Object.entries(fulltimeFaByProject).forEach(([pid, fa]) => {
          if (pid === dominantProjectId || fa <= 0) return;
          if (!byProject[pid]) {
            const info = empProjectInfo[pid] || {};
            byProject[pid] = {
              project_id: info.project_id || null,
              project_number: info.project_number || '',
              project_name: info.project_name || '',
              employees: [],
              employee_deductions: [],
              total_salary: 0,
              total_ot: 0,
              total_field_allowance: 0,
              total_allowance: 0,
              total_bonus: 0,
              total_crl: 0,
              total_payable: 0,
            };
          }
          byProject[pid].employees.push({
            employee_id: emp.employee_id,
            name: emp.name,
            amount: 0,
            ot_amount: 0,
            field_allowance_amount: fa,
            allowance: 0,
            salary_per_day: spd,
            rate: 0,
          });
          byProject[pid].total_field_allowance += fa;
        });
      }

      const fi = fieldIndexMap[empId];
      const rowData = watchedSalaryData[fi] || {};
      const f = fields[fi] || {};
      const bonusAmt = parseFloat(rowData?.revenue?.bonus ?? f?.revenue?.bonus ?? 0) || 0;
      if (bonusAmt > 0) {
        ensureDominantBucket();
        byProject[dominantProjectId].total_bonus += bonusAmt;
      }

      // "ที่ต้องจ่าย" ทั้งก้อนของพนักงานคนนี้
      // สูตรเดียวกับ ActualNetCell เป๊ะ (cells.js) ซึ่งเป็นตัวที่ขึ้นจริงในคอลัมน์ "ที่ต้องจ่ายพนักงาน" — ไม่หักสาย
      // ใช้ actualSalaryAmt (ชม.จริง) แทน revenue.salary (ฟอร์ม) ทุกคนมีค่านี้เสมอ (ไม่มีเงื่อนไขเหมือน bonusAmt>0)
      const actualSalaryAmt = empSummaryMap[empId]?.actualSalaryAmt || 0;
      const otAmtForNet = parseFloat(rowData?.revenue?.overtime ?? f?.revenue?.overtime ?? 0) || 0;
      const allowancesAmt = parseFloat(
        rowData?.revenue?.allowances ?? f?.revenue?.allowances ?? f?.total_extra_day ?? 0,
      ) || 0;
      const fieldAllowAmt = parseFloat(rowData?.revenue?.field_allowance ?? f?.revenue?.field_allowance ?? 0) || 0;
      const taxAmt = parseFloat(rowData?.expenses?.tax ?? f?.expenses?.tax ?? 0) || 0;
      const ssoAmt = parseFloat(rowData?.expenses?.sso ?? f?.expenses?.sso ?? 0) || 0;
      const crlAmt = parseFloat(rowData?.expenses?.crl ?? f?.expenses?.crl ?? 0) || 0;
      const otherAmt = parseFloat(rowData?.expenses?.other ?? f?.expenses?.other ?? 0) || 0;
      // ยอดยกมาลงงวดนี้ (PayrollCarryover) — บวก/ลบสุทธิเข้า "ที่ต้องจ่าย" ต่อโครงการเหมือน bonus/crl
      // (เป็นยอดต่อคน ไม่ผูกโครงการ — ไหลผ่าน remainder แบบเดียวกับ payableAmt สำหรับ DAILY)
      const carryDeltaAmt = (Number(carryoverByEmpId?.[empId]?.addTotal) || 0)
        - (Number(carryoverByEmpId?.[empId]?.deductTotal) || 0);
      // หัก CRL (เบิกเงินเดือนล่วงหน้า) — หักออกจาก "ที่ต้องจ่าย" ต่อโครงการเหมือน tax/sso/other
      // (ยอดเบิก CRL เดิมถูกลงงบ/โครงการไปแล้วตอนกดจ่าย — หักตรงนี้เพื่อไม่ให้จ่ายซ้ำในงวดนี้)
      const payableAmt = actualSalaryAmt + otAmtForNet + allowancesAmt + fieldAllowAmt + bonusAmt
        - taxAmt - ssoAmt - crlAmt - otherAmt + carryDeltaAmt;

      if (isFulltime) {
        // FULLTIME: ค่าแรง/OT/เบี้ยเลี้ยง/หัก ยังลงก้อนเดียวที่โครงการหลัก — ยกเว้น "ค่าออกสนาม" (FA) ที่แยก
        // ตามโครงการที่ลงเวลาจริงรายวัน (fulltimeFaByProject) เพื่อให้คอลัมน์ "ที่ต้องจ่าย" ต่อโครงการ reconcile
        // กับคอลัมน์ "ค่าออกสนาม" ที่แยกไปแล้ว
        ensureDominantBucket();
        const faNonDominant = Object.entries(fulltimeFaByProject)
          .reduce((s, [pid, fa]) => (pid === dominantProjectId ? s : s + fa), 0);
        byProject[dominantProjectId].total_payable = (byProject[dominantProjectId].total_payable || 0) + payableAmt - faNonDominant;
        byProject[dominantProjectId].total_crl = (byProject[dominantProjectId].total_crl || 0) + crlAmt;
        Object.entries(fulltimeFaByProject).forEach(([pid, fa]) => {
          if (pid === dominantProjectId || fa <= 0 || !byProject[pid]) return;
          byProject[pid].total_payable = (byProject[pid].total_payable || 0) + fa;
        });
        // รายเดือนไม่เคยกระจายข้ามโครงการอยู่แล้ว (บังคับลงโครงการหลักเดียวเสมอ) — เลยลง 100% ตรงๆ ไม่ต้องประมาณ
        byProject[dominantProjectId].employee_deductions.push({
          employee_id: empId, bonus: bonusAmt, sso: ssoAmt, tax: taxAmt, crl: crlAmt, other: otherAmt,
        });
      } else {
        // DAILY: ส่วนค่าแรง/OT/ค่าออกสนาม/เบี้ยเลี้ยง ผูกกับ day-cell/โครงการอยู่แล้ว (empProjectGross มาจาก
        // calcCheckinActualSalary + otBreakdownMap + faBreakdownMap ต่อวันจริง) เลยลงตรงๆ แบบ exact ไม่ต้องประมาณ
        // เหลือแค่ "remainder" (bonus/หัก ณ ที่จ่าย/ประกันสังคม/manual ที่ไม่ผูก timestamp) ที่ไม่มี project
        // ผูกอยู่ในข้อมูลเลย — ส่วนนี้กระจายตามสัดส่วนรายได้ต่อโครงการ (ไม่มีทางรู้ที่มาจริง)
        // **ยกเว้น "หักอื่นๆ" (otherAmt) และ "หัก CRL" (crlAmt)** — เป็นยอดต่อคน ไม่ผูกกับงานโครงการไหน
        // user ต้องการให้ลงเต็มก้อนที่โครงการที่พนักงานคนนี้ลงเวลามากสุด (dominantProjectId) เหมือน total_bonus
        // → เอา otherAmt/crlAmt ออกจาก remainder ที่กระจายตาม ratio แล้วหักเต็มก้อนที่ dominant ทีหลัง
        // เพื่อให้คอลัมน์ "ที่ต้องจ่าย" ต่อโครงการ reconcile กับคอลัมน์ "หักอื่นๆ"/"หัก CRL"
        const grossEntries = Object.entries(empProjectGross);
        const grossSum = grossEntries.reduce((s, [, v]) => s + v, 0);
        const remainder = payableAmt + otherAmt + crlAmt - grossSum;
        if (grossSum > 0) {
          grossEntries.forEach(([pid, v]) => {
            if (!byProject[pid]) return;
            const ratio = v / grossSum;
            byProject[pid].total_payable = (byProject[pid].total_payable || 0) + v + remainder * ratio;
            // total_crl ไม่กระจาย — ลงเต็มก้อนที่ dominant ด้านล่างแทน
            // เงินได้อื่นๆ/หัก ต่อคนต่อโครงการ (สำหรับ laborProjectReceiptPDF.js) — กระจายตามสัดส่วนเดียวกับ
            // total_payable เป๊ะ (best-effort เพราะข้อมูลต้นทางไม่ผูกโครงการไว้เลยสำหรับ 3 ค่านี้)
            byProject[pid].employee_deductions.push({
              employee_id: empId,
              bonus: bonusAmt * ratio,
              sso: ssoAmt * ratio,
              tax: taxAmt * ratio,
              crl: 0, // ลงเต็มก้อนที่ dominant ด้านล่างแทน
              other: 0, // ลงเต็มก้อนที่ dominant ด้านล่างแทน
            });
          });
          if (otherAmt > 0 || crlAmt > 0) {
            ensureDominantBucket();
            byProject[dominantProjectId].total_payable = (byProject[dominantProjectId].total_payable || 0) - otherAmt - crlAmt;
            byProject[dominantProjectId].total_crl = (byProject[dominantProjectId].total_crl || 0) + crlAmt;
            const domDed = byProject[dominantProjectId].employee_deductions
              .find((d) => String(d.employee_id) === String(empId));
            if (domDed) {
              domDed.other = (domDed.other || 0) + otherAmt;
              domDed.crl = (domDed.crl || 0) + crlAmt;
            } else {
              byProject[dominantProjectId].employee_deductions.push({ employee_id: empId, bonus: 0, sso: 0, tax: 0, crl: crlAmt, other: otherAmt });
            }
          }
        } else {
          // ไม่มี checkin เลยทั้งงวด (เช่น มีแต่ bonus) → ตกไปกอง dominant/no_project เหมือนเดิม
          ensureDominantBucket();
          byProject[dominantProjectId].total_payable = (byProject[dominantProjectId].total_payable || 0) + payableAmt;
          byProject[dominantProjectId].total_crl = (byProject[dominantProjectId].total_crl || 0) + crlAmt;
          byProject[dominantProjectId].employee_deductions.push({
            employee_id: empId, bonus: bonusAmt, sso: ssoAmt, tax: taxAmt, crl: crlAmt, other: otherAmt,
          });
        }
      }

      // ยอดติดลบที่งวดนี้ "ยกไปงวดหน้า" แล้ว (PayrollCarryover auto, source_period = งวดนี้) — บวกคืนเต็มก้อน
      // ที่โครงการหลัก ให้ "ที่ต้องจ่าย" ของคนนี้รวมทุกโครงการ = 0 (ไม่งั้นยอดติดลบถูกหักทั้งงวดนี้ + งวดหน้าซ้ำ)
      // dominant_employee_ids = คนที่ลงเต็มก้อนที่โครงการนี้ — HrReportV2 ใช้หาโครงการตอนปรับยอดยกไปตอนกดบันทึก
      const carryOutAmt = sumCarryOut(carryoverOutByEmpId?.[empId]);
      if (carryOutAmt > 0) {
        ensureDominantBucket();
        byProject[dominantProjectId].total_payable = (byProject[dominantProjectId].total_payable || 0) + carryOutAmt;
      }
      if (byProject[dominantProjectId]) {
        if (!byProject[dominantProjectId].dominant_employee_ids) byProject[dominantProjectId].dominant_employee_ids = [];
        byProject[dominantProjectId].dominant_employee_ids.push(empId);
      }
    });

    Object.values(byProject).forEach((p) => {
      p.total_salary = Math.round(p.total_salary * 100) / 100;
      p.total_ot = Math.round(p.total_ot * 100) / 100;
      p.total_field_allowance = Math.round(p.total_field_allowance * 100) / 100;
      p.total_allowance = Math.round(p.total_allowance * 100) / 100;
      p.total_bonus = Math.round((p.total_bonus || 0) * 100) / 100;
      p.total_crl = Math.round((p.total_crl || 0) * 100) / 100;
      p.total_payable = Math.round((p.total_payable || 0) * 100) / 100;
      p.total_spent = p.total_salary + p.total_ot;
    });

    return Object.values(byProject).sort((a, b) => {
      if (!a.project_id || !b.project_id) return 0;
      return String(b.project_id).localeCompare(String(a.project_id));
    });
  }, [timestamp, otBreakdownMap, faBreakdownMap, fields, fieldIndexMap, watchedSalaryData, empSummaryMap, carryoverByEmpId, carryoverOutByEmpId]);

  useEffect(() => {
    if (onProjectDashboardChange) onProjectDashboardChange(projectDashboard);
  }, [projectDashboard, onProjectDashboardChange]);

  // รายงาน empSummaryMap ขึ้น parent ไว้ใช้ตอนสร้าง PDF สรุปทั้งหน้า (ต้องการ วันทำงาน/OT ชม. ต่อคน
  // ที่คำนวณตรงกับคอลัมน์ "สรุป" ในตารางนี้เป๊ะๆ — เลี่ยงคำนวณซ้ำอีกชุดฝั่ง PDF)
  useEffect(() => {
    if (onEmpSummaryChange) onEmpSummaryChange(empSummaryMap);
  }, [empSummaryMap, onEmpSummaryChange]);

  // sync SSO + salary column ให้ตรงกับค่าที่คำนวณจริง (displayAmt รวม salary_override) — ต้องอยู่หลัง empSummaryMap
  // ข้าม sync ตอนงวด locked (readOnly) เพราะ parent (HrReportV2.js) reset() ยอดที่บันทึกจริงมาให้แล้ว —
  // ถ้าไม่ข้าม จะเขียนทับยอด locked ด้วยค่าคำนวณสดปัจจุบันทันที (ซึ่งอาจไม่ตรงกับตอนกดบันทึกแล้ว
  // ถ้า checkin/leave/attendance rules ถูกแก้ไขภายหลัง) ทำให้ก่อน/หลังบันทึกเงินเดือนเห็นค่าไม่ตรงกัน
  useEffect(() => {
    if (readOnly || !setValue || !fields.length) return;
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const fi = fieldIndexMap[empId];
      if (fi === undefined) return;
      const f = fields[fi];
      const mf = f?.manual_fields || [];
      const summary = empSummaryMap[empId] || {};
      const displayAmt = summary.displayAmt || 0;
      const salaryBase =
        salaryMode === 'work-time' ? summary.actualSalaryAmt || 0 : displayAmt;
      setValue(`salaryData.${fi}.revenue.salary`, salaryBase.toFixed(2));
      if (!f?.sso_tax?.status) return;
      const ssoPaid = f.sso_paid || 0;
      const ssoRate = f.sso_tax?.number || 5;
      let ssoAuto = salaryBase * (ssoRate / 100);
      if (ssoAuto > 0) {
        if (ssoAuto + ssoPaid > 875) ssoAuto = Math.max(0, 875 - ssoPaid);
        if (ssoAuto + ssoPaid < 83) ssoAuto = Math.max(0, 83 - ssoPaid);
      }
      const ssoVal = ssoAuto.toFixed(2);
      if (!autoValuesRef.current[empId]) autoValuesRef.current[empId] = {};
      autoValuesRef.current[empId].sso = ssoVal;
      if (!mf.includes('sso'))
        setValue(`salaryData.${fi}.expenses.sso`, ssoVal);
    });
  }, [empSummaryMap, fields, salaryMode, readOnly]); // eslint-disable-line

  // sync allowances column ให้ตรงกับสูตรเดียวกับ popup (buildAllowanceContent)
  // ข้าม sync ตอน readOnly เหมือน effect ข้างบน (เหตุผลเดียวกัน — ห้ามเขียนทับยอด locked)
  useEffect(() => {
    if (readOnly || !setValue || !fields.length) return;
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const fi = fieldIndexMap[empId];
      if (fi === undefined) return;
      const mf = fields[fi]?.manual_fields || [];
      const rateFromCheckin =
        (emp.checkins || []).find((c) => (c.extra_day || 0) > 0)?.extra_day ||
        0;
      const rateFromManual =
        days
          .map((d) => manualByKey[`${empId}-${d}`]?.allowance_amount || 0)
          .find((a) => a > 0) || 0;
      const dailyRate = rateFromCheckin || rateFromManual;
      let allowTotal = 0;
      days.forEach((d) => {
        const ck = checkinByKey[`${empId}-${d}`];
        const entry = manualByKey[`${empId}-${d}`];
        const hasWork = ck || (entry?.hours > 0 && !entry?.timestamp_ref);
        if (!hasWork) return;
        const amt = ck?.extra_day || entry?.allowance_amount || dailyRate;
        if (!amt) return;
        allowTotal += amt;
      });
      const allowVal = allowTotal.toFixed(2);
      if (!autoValuesRef.current[empId]) autoValuesRef.current[empId] = {};
      autoValuesRef.current[empId].allowances = allowVal;
      if (!mf.includes('allowances'))
        setValue(`salaryData.${fi}.revenue.allowances`, allowVal);
    });
  }, [checkinByKey, manualByKey, fields, readOnly]); // eslint-disable-line

  // sync field_allowance column — ดึงจาก faBreakdownMap ที่คำนวณครั้งเดียวไว้แล้ว (ตรงกับ popup + modal เป๊ะๆ)
  // ข้าม sync ตอน readOnly เหมือน 2 effect ข้างบน (เหตุผลเดียวกัน — ห้ามเขียนทับยอด locked)
  useEffect(() => {
    if (readOnly || !setValue || !fields.length) return;
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const fi = fieldIndexMap[empId];
      if (fi === undefined) return;
      const faVal = (faBreakdownMap[empId]?.total || 0).toFixed(2);
      if (!autoValuesRef.current[empId]) autoValuesRef.current[empId] = {};
      autoValuesRef.current[empId].field_allowance = faVal;
      if (!(fields[fi]?.manual_fields || []).includes('field_allowance')) {
        setValue(`salaryData.${fi}.revenue.field_allowance`, faVal);
      }
    });
  }, [timestamp, fields, faBreakdownMap, readOnly]); // eslint-disable-line

  // populate autoValuesRef สำหรับ fields ที่ไม่มี useEffect sync (OT, tax, late, bonus, other)
  useEffect(() => {
    if (!fields.length) return;
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const fi = fieldIndexMap[empId];
      if (fi === undefined) return;
      const f = fields[fi];
      if (!autoValuesRef.current[empId]) autoValuesRef.current[empId] = {};
      const av = autoValuesRef.current[empId];
      const mf = f?.manual_fields || [];
      if (!mf.includes('overtime'))
        av.overtime = Number(
          f?.revenue?.overtime ?? f?.totalOtAmount ?? 0,
        ).toFixed(2);
      if (!mf.includes('tax'))
        av.tax = Number(
          CalTax(f?.salary_per_day * f?.totalWork, f?.tax_type) || 0,
        ).toFixed(2);
      if (!mf.includes('late'))
        av.late = Number(f?.expenses?.late ?? 0).toFixed(2);
      if (!mf.includes('bonus'))
        av.bonus = Number(f?.revenue?.bonus ?? 0).toFixed(2);
      if (!mf.includes('other'))
        av.other = Number(f?.expenses?.other ?? 0).toFixed(2);
    });
  }, [fields, timestamp, fieldIndexMap]); // eslint-disable-line

  // totalsBase: work/salary/OT only — net is handled by TotalNetCell sub-component (avoids watch subscription here)
  const totalsBase = useMemo(() => {
    return (timestamp?.rows || []).reduce(
      (acc, emp) => {
        const id = String(emp.employee_id);
        const ma = getManualAmt(id, emp.salary_per_day, emp.emp_type);
        acc.work += emp.totalWork || 0;
        acc.salary += (emp.totalAmount || 0) + ma;
        acc.ot += otTotalMap[id] ?? emp.totalOtAmount ?? 0;
        acc.actualSalary += empSummaryMap[id]?.actualSalaryAmt || 0;
        return acc;
      },
      { work: 0, salary: 0, ot: 0, actualSalary: 0 },
    );
    // eslint-disable-next-line
  }, [timestamp, fieldIndexMap, manualWorkData, empSummaryMap, otTotalMap]);

  // ── salary override dialog ────────────────────────────────────────────
  const openDetail = (checkin, name, spd, showLate = false, empId = null) => {
    if (readOnly) return;
    const dr = spd || checkin?.salary_per_day || 0;
    const ovr = checkin?.salary_override;
    const initRate = ovr?.rate ?? checkin?.effectiveRate ?? checkin?.rate ?? 1;
    const initHours = ovr?.hours ?? Math.max(checkin?.totalHour ?? 8, 8);
    // นับเป็น 8 ชม. จริงๆ ต้องเช็คจาก hour_override (field ที่ใช้คำนวณค่าแรงตามชม.จริงจริงๆ)
    setForceFullHours(checkin?.hour_override?.hours === 8);

    // field_allowance ของพนักงาน — คำนวณไว้แล้วครั้งเดียวใน faBreakdownMap
    const faInfo = faBreakdownMap[String(empId)];
    const faEnabled = faInfo?.faEnabled || false;
    const faAmt = faInfo?.faDay ?? globalFieldRate;

    // default checkbox: มาจากค่าที่ set ไว้ตรงๆ เท่านั้น (ผ่านคำขอค่าออกสนามที่อนุมัติแล้ว หรือ HR ติ๊กเอง) — ไม่ auto
    // จาก project prefix แล้ว ยกเว้นตอนดูงวดเก่าที่ยังไม่บันทึกเงินเดือนด้วยโหมด "เก่า" (fieldAllowanceMode)
    const savedFAForDialog = ovr?.field_allowance_day;
    let dlgFA = savedFAForDialog === true;
    if (fieldAllowanceMode === 'old' && savedFAForDialog == null) {
      const pn = (checkin?.project_number || '').toLowerCase().trim();
      dlgFA = pn.startsWith('js') && !pn.startsWith('jsid');
    }
    setDlgFieldAllow(dlgFA);

    setDetailData({
      checkin,
      name,
      spd: dr,
      showLate,
      empId,
      faEnabled,
      faAmt,
    });
    setEditAmtManual(ovr?.day_rate != null ? String(ovr.day_rate) : null);
    setDetailHours(String(initHours));
    setDetailRate(initRate);
    setEditCheckIn(checkin?.checkInTime || '');
    setEditCheckOut(checkin?.checkOutTime || '');
    setEditProjectId(checkin?.project_id ? String(checkin.project_id) : '');
    setDetailOpen(true);
  };
  const saveOverride = async () => {
    if (!detailData?.checkin?._id || !onSalaryOverride) return;
    const dayRateToSave = parseFloat(
      editAmtManual ?? String(detailData?.spd || 0),
    );
    // เตือนซ้ำก่อนบันทึกจริง ถ้าค่าแรง/วันต่างจากปกติของพนักงานคนนี้เกิน 5 เท่า (กันพิมพ์เลขผิด)
    const origDrForSave = detailData?.spd || 0;
    const isDrSuspicious =
      origDrForSave > 0 &&
      dayRateToSave > 0 &&
      (dayRateToSave > origDrForSave * 5 || dayRateToSave < origDrForSave / 5);
    if (
      isDrSuspicious &&
      !(await confirmAsync(
        `ค่าแรง/วันที่กรอก (฿${dayRateToSave.toLocaleString()}) ต่างจากค่าแรงปกติของพนักงานคนนี้ (฿${origDrForSave.toLocaleString()}/วัน) มาก — แน่ใจว่าพิมพ์ถูกแล้วใช่ไหม?`,
      ))
    ) {
      return;
    }
    setSaving(true);
    try {
      const totalToSave = Number((detailRate * dayRateToSave).toFixed(2));
      await onSalaryOverride(detailData.checkin._id, {
        amount: totalToSave,
        rate: detailRate,
        day_rate: dayRateToSave,
        ...(forceFullHours && { hours: 8 }),
        edited_by: editorName,
      });
      // "นับเป็น 8 ชม." ต้องเขียนลง hour_override ด้วย (field เดียวกับปุ่มลบ/คืนชั่วโมงทำงาน)
      // เพราะเป็น field ที่ค่าแรงตามชม.จริง/PDF/รายงาน ใช้คำนวณจริง ไม่ใช่ salary_override.hours ที่ใช้แค่โชว์ label
      const wasForced = detailData?.checkin?.hour_override?.hours === 8;
      if (onHourOverride) {
        if (forceFullHours) {
          await onHourOverride(detailData.checkin._id, {
            hours: 8,
            // ส่งชั่วโมงจริงที่คำนวณถูกต้องแล้ว (หักพักเที่ยง/clamp เวลางาน) ไปเก็บเป็นค่าเดิม
            // กันไม่ให้ backend เดาเองแบบ raw diff (ไม่แม่น)
            previous_hours: detailData?.checkin?.totalHour ?? null,
            edited_by: editorName,
          });
        } else if (wasForced) {
          await onHourOverride(detailData.checkin._id, {
            hours: null,
            edited_by: editorName,
          });
        }
      }
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setSaving(false);
    }
  };
  const saveFieldAllowDay = async (checked) => {
    if (!detailData?.checkin?._id || !onSalaryOverride) return;
    setDlgFieldAllow(checked);
    try {
      await onSalaryOverride(detailData.checkin._id, {
        field_allowance_day: checked,
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
    } catch (e) {
      setDlgFieldAllow(!checked);
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกค่าออกสนามไม่สำเร็จ',
        { status: e?.response?.status },
      );
    }
  };

  const saveSkipLate = async (newEnabled) => {
    if (!detailData?.checkin?._id || !onSkipLateDeduct) return;
    setSkipLateSaving(true);
    try {
      await onSkipLateDeduct(detailData.checkin._id, {
        enabled: newEnabled,
        edited_by: editorName,
      });
      setDetailData((prev) => ({
        ...prev,
        checkin: { ...prev.checkin, skip_late_deduct: newEnabled },
        showLate: newEnabled ? true : prev.showLate,
      }));
      onRefreshTimestamp?.();
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setSkipLateSaving(false);
    }
  };

  const saveSkipLateDv = async (newEnabled) => {
    if (!dvData?.checkin?._id || !onSkipLateDeduct) return;
    setSkipLateDvSaving(true);
    try {
      await onSkipLateDeduct(dvData.checkin._id, {
        enabled: newEnabled,
        edited_by: editorName,
      });
      setDvData((prev) => ({
        ...prev,
        checkin: { ...prev.checkin, skip_late_deduct: newEnabled },
      }));
      onRefreshTimestamp?.();
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setSkipLateDvSaving(false);
    }
  };

  const clearOverride = async () => {
    if (!(await confirmAsync('ล้างการแก้ไขและใช้ค่าคำนวณอัตโนมัติ?'))) return;
    setSaving(true);
    try {
      await onSalaryOverride(detailData.checkin._id, { amount: null });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ล้างไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const clearTimeOverride = async () => {
    if (!onCheckInTimeClear) return;
    if (!(await confirmAsync('คืนค่าเวลาเข้าออกกลับเป็นค่าเดิม?'))) return;
    setSaving(true);
    try {
      await onCheckInTimeClear(detailData.checkin._id);
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ล้างไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const clearProjectOverride = async () => {
    if (!onProjectClear) return;
    if (!(await confirmAsync('คืนค่าโปรเจคกลับเป็นค่าเดิม?'))) return;
    setSaving(true);
    try {
      await onProjectClear(detailData.checkin._id);
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ล้างไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const deleteWorkHours = async () => {
    if (!onHourOverride) return;
    if (
      !(await confirmAsync(
        'ลบชั่วโมงทำงานออกจากการคำนวณเงินเดือน? (สามารถคืนกลับได้)',
      ))
    )
      return;
    setSaving(true);
    try {
      await onHourOverride(detailData.checkin._id, {
        hours: 0,
        previous_hours: detailData?.checkin?.totalHour ?? null,
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const restoreWorkHours = async () => {
    if (!onHourOverride) return;
    if (!(await confirmAsync('คืนชั่วโมงทำงานกลับ (ใช้ค่าคำนวณอัตโนมัติ)?'))) return;
    setSaving(true);
    try {
      await onHourOverride(detailData.checkin._id, {
        hours: null,
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  // ล้างการติ๊ก "นับเป็น 8 ชม." ที่บันทึกไว้ (เผลอติ๊ก) — คืนกลับไปใช้ชั่วโมงทำงานจริงตามปกติ
  const clearForceFullHours = async () => {
    if (!onHourOverride) return;
    if (!(await confirmAsync('ล้างการติ๊ก "นับเป็น 8 ชม." กลับไปใช้ชั่วโมงทำงานจริง?')))
      return;
    setSaving(true);
    try {
      await onHourOverride(detailData.checkin._id, {
        hours: null,
        edited_by: editorName,
      });
      setForceFullHours(false);
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const deleteOtHours = async () => {
    if (!onOtOverride) return;
    if (
      !(await confirmAsync(
        'ลบชั่วโมง OT ออกจากการคำนวณเงินเดือน? (สามารถคืนกลับได้)',
      ))
    )
      return;
    setSaving(true);
    try {
      await onOtOverride(detailData.checkin._id, {
        disabled: true,
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const restoreOtHours = async () => {
    if (!onOtOverride) return;
    if (!(await confirmAsync('คืนชั่วโมง OT กลับ?'))) return;
    setSaving(true);
    try {
      await onOtOverride(detailData.checkin._id, {
        disabled: false,
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const deleteManualFromDialog = async () => {
    if (!(await confirmAsync('ลบชั่วโมงที่เพิ่มออก?'))) return;
    const empId = detailData?.empId;
    const day = dayjs(detailData?.checkin?.checkIn).date();
    const entry = empId ? getEntry(empId, day) : null;
    if (!entry?._id) {
      showAlert('ไม่พบข้อมูลชั่วโมงที่เพิ่ม');
      return;
    }
    setSaving(true);
    try {
      if (onManualDelete) await onManualDelete(entry._id);
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ลบไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const saveCheckInTime = async () => {
    if (!detailData?.checkin?._id || !onCheckInTimeUpdate) return;
    if (!editCheckIn && !editCheckOut) return;
    setCheckTimeSaving(true);
    try {
      await onCheckInTimeUpdate(detailData.checkin._id, {
        ...(editCheckIn && { checkInTime: editCheckIn }),
        ...(editCheckOut && { checkOutTime: editCheckOut }),
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setCheckTimeSaving(false);
    }
  };

  const saveProject = async () => {
    if (!detailData?.checkin?._id || !onProjectUpdate) return;
    setCheckTimeSaving(true);
    try {
      await onProjectUpdate(detailData.checkin._id, {
        project_id: editProjectId || null,
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setCheckTimeSaving(false);
    }
  };

  // ── add hours dialog ─────────────────────────────────────────────────
  const openAddHours = (empId, empName, day, spd, allowancePerDay = 0) => {
    if (readOnly) return;
    const ex = getEntry(empId, day);
    const dow = dayjs(`${year}-${month + 1}-${day}`).day();
    const isHolidayOrSunday = dow === 0 || holidayDaySet.has(day);
    const resolvedAllowance =
      allowancePerDay > 0 ? allowancePerDay : ex?.allowance_amount ?? 0;
    setAddTarget({
      empId,
      empName,
      day,
      spd,
      isWeekend: isHolidayOrSunday,
      allowancePerDay: resolvedAllowance,
    });
    const initHours = ex?.hours != null ? ex.hours : null;
    const initRate = ex?.work_rate ?? (isHolidayOrSunday ? 2 : 1);
    // ช่อง "ค่าแรง (บาท)" คือค่าแรง/วัน (8 ชม.) — back-calc จาก amount เดิมถ้ามี, ไม่มีปล่อยว่าง (fallback = spd ตอนคำนวณ)
    const initDayRate =
      ex?.amount != null && initHours > 0
        ? (((ex.amount / initHours) * 8) / (initRate || 1)).toFixed(2)
        : '';
    // pre-fill times: ถ้ามี entry เดิม back-calc จาก hours, ไม่มีใช้ default 08:00-17:00
    setAddCheckIn('08:00');
    setAddCheckOut(
      initHours != null ? hoursToCheckOutTime(initHours) : '17:00',
    );
    setAddAmt(initDayRate);
    setAddWorkRate(initRate);
    setAddIncludeAllowance((ex?.allowance_amount ?? 0) > 0);
    setAddProjectId(ex?.project ? String(ex.project) : '');
    setAddOpen(true);
  };
  const saveAddHours = async () => {
    const h = calcWorkHours(addCheckIn, addCheckOut);
    if (h <= 0) return;
    const rate = addWorkRate;
    // ช่อง "ค่าแรง (บาท)" ว่าง → ใช้ค่าแรง 8 ชม. (spd) ของพนักงานคนนั้นเป็นค่าเริ่มต้น
    const dayRateOvr = parseFloat(addAmt);
    const dayRate =
      !isNaN(dayRateOvr) && addAmt !== '' ? dayRateOvr : addTarget?.spd || 0;
    const total = Number(((h / 8) * dayRate * rate).toFixed(2));
    const payload = {
      employee_id: String(addTarget.empId),
      employee_name: addTarget.empName,
      date: `${year}-${String(month + 1).padStart(2, '0')}-${String(
        addTarget.day,
      ).padStart(2, '0')}`,
      hours: h,
      salary_per_day: dayRate,
      edited_by: editorName,
      work_rate: rate,
      allowance_amount: addIncludeAllowance
        ? addTarget.allowancePerDay || 0
        : 0,
      amount: total,
      project_id: addProjectId || undefined,
    };
    setAddSaving(true);
    try {
      if (onManualSave) await onManualSave(payload);
    } finally {
      setAddSaving(false);
    }
    setAddOpen(false);
    setAddTarget(null);
    setAddCheckIn('');
    setAddCheckOut('');
    setAddAmt('');
    setAddProjectId('');
  };
  const deleteManual = async () => {
    const ex = getEntry(addTarget.empId, addTarget.day);
    if (!ex?._id) return;
    setAddSaving(true);
    try {
      if (onManualDelete) await onManualDelete(ex._id);
    } finally {
      setAddSaving(false);
    }
    setAddOpen(false);
    setAddTarget(null);
    setAddCheckIn('');
    setAddCheckOut('');
    setAddAmt('');
  };

  // ── Bulk day dialog ───────────────────────────────────────────────────────
  const openBulkDay = (day) => {
    if (readOnly) return;
    setBulkDay(day);
    setBulkTab(0);
    setBulkCheckIn('08:00');
    setBulkCheckOut('17:00');
    setBulkRate(1);
    setBulkDayOpen(true);
  };
  const saveBulkDay = async () => {
    const inMins = timeToMins(bulkCheckIn);
    const outMins = timeToMins(bulkCheckOut);
    const h = outMins > inMins ? (outMins - inMins) / 60 : 0;
    if (h <= 0 || !onManualSave) return;
    const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(
      bulkDay,
    ).padStart(2, '0')}`;
    setBulkSaving(true);
    try {
      const empRows = timestamp?.rows || [];
      for (const emp of empRows) {
        const empId = String(emp.employee_id);
        const hasCheckin = !!checkinByKey[`${empId}-${bulkDay}`];
        const hasManual = !!manualByKey[`${empId}-${bulkDay}`];
        if (!hasCheckin && !hasManual) {
          // eslint-disable-next-line no-await-in-loop
          await onManualSave({
            employee_id: empId,
            employee_name: emp.name || '',
            date,
            hours: h,
            salary_per_day: emp.salary_per_day || 0,
            edited_by: editorName,
            work_rate: bulkRate,
            allowance_amount: 0,
            amount: null,
          });
        }
      }
      await onRefreshTimestamp?.();
      setBulkDayOpen(false);
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setBulkSaving(false);
    }
  };

  const saveBulkDayRate = async () => {
    if (!onSalaryOverride && !onManualSave) return;
    const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(
      bulkDay,
    ).padStart(2, '0')}`;
    setBulkSaving(true);
    try {
      const empRows = timestamp?.rows || [];
      for (const emp of empRows) {
        const empId = String(emp.employee_id);
        const ck = checkinByKey[`${empId}-${bulkDay}`];
        const manEntry = manualByKey[`${empId}-${bulkDay}`];
        if (ck && onSalaryOverride) {
          const spd = emp.salary_per_day || 0;
          // eslint-disable-next-line no-await-in-loop
          await onSalaryOverride(ck._id, {
            amount: bulkRate * spd,
            rate: bulkRate,
            day_rate: spd,
            edited_by: editorName,
          });
        } else if (manEntry && onManualSave) {
          // eslint-disable-next-line no-await-in-loop
          await onManualSave({
            employee_id: empId,
            employee_name: emp.name || '',
            date,
            hours: manEntry.hours,
            salary_per_day: manEntry.salary_per_day || emp.salary_per_day || 0,
            edited_by: editorName,
            work_rate: bulkRate,
            allowance_amount: manEntry.allowance_amount || 0,
            amount: null,
          });
        }
      }
      await onRefreshTimestamp?.();
      setBulkDayOpen(false);
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setBulkSaving(false);
    }
  };

  // ── OT dialog ────────────────────────────────────────────────────────────
  const openAddOt = (empId, empName, day, spd, approvedOt = null, projectInfo = null) => {
    if (readOnly) return;
    const ex = getEntry(empId, day);
    const dow = dayjs(`${year}-${month + 1}-${day}`).day();
    const isHolidayOrSunday = dow === 0 || holidayDaySet.has(day);
    const rate =
      ex?.ot_rate != null
        ? ex.ot_rate
        : approvedOt?.rate != null
        ? approvedOt.rate
        : isHolidayOrSunday ? 3.0 : 1.5;
    // ชั่วโมง: ใช้ manual entry ก่อน → ถ้าไม่มีใช้ approved OT จาก checkin
    const hours =
      ex?.ot_hours != null ? ex.ot_hours : approvedOt?.hours || null;
    setAddOtTarget({
      empId,
      empName,
      day,
      spd,
      projectName: projectInfo?.projectName || null,
      projectNumber: projectInfo?.projectNumber || null,
    });
    setAddOtHours(hours != null ? String(hours) : '');
    setAddOtRate(rate);
    setAddOtOpen(true);
  };
  const saveAddOt = async () => {
    const h = parseFloat(addOtHours);
    if (isNaN(h) || h <= 0) return;
    const payload = {
      employee_id: String(addOtTarget.empId),
      employee_name: addOtTarget.empName,
      date: `${year}-${String(month + 1).padStart(2, '0')}-${String(
        addOtTarget.day,
      ).padStart(2, '0')}`,
      ot_hours: h,
      ot_rate: addOtRate,
      ot_amount: null,
      edited_by: editorName,
    };
    setAddOtSaving(true);
    try {
      if (onManualSave) await onManualSave(payload);
      // ไม่ต้อง onRefreshTimestamp — คอลัมน์ OT คำนวณสดจาก otBreakdownMap (manualByKey) อยู่แล้ว
      // ไม่ต้องรอ backend refetch ทั้งเดือน (การ refetch นี้ทำให้หน้าโหลดช้าลงโดยไม่จำเป็น)
    } finally {
      setAddOtSaving(false);
    }
    setAddOtOpen(false);
    setAddOtTarget(null);
    setAddOtHours('');
    setAddOtRate(1.5);
  };
  const clearManualOt = async () => {
    const payload = {
      employee_id: String(addOtTarget.empId),
      employee_name: addOtTarget.empName,
      date: `${year}-${String(month + 1).padStart(2, '0')}-${String(
        addOtTarget.day,
      ).padStart(2, '0')}`,
      ot_hours: null,
      ot_rate: null,
      ot_amount: null,
      edited_by: editorName,
    };
    setAddOtSaving(true);
    try {
      if (onManualSave) await onManualSave(payload);
      // ไม่ต้อง onRefreshTimestamp — เหตุผลเดียวกับ saveAddOt ด้านบน
    } finally {
      setAddOtSaving(false);
    }
    setAddOtOpen(false);
    setAddOtTarget(null);
    setAddOtHours('');
    setAddOtRate(1.5);
  };
  if (!timestamp?.rows?.length) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
        ไม่มีข้อมูลพนักงานในช่วงเวลานี้
      </div>
    );
  }

  // ── computed vars for dialog props ─────────────────────────────────────
  // ชั่วโมงทำงาน 08:00–17:00 ไม่นับพักเที่ยง 12:00–13:00 (สูงสุด 8 ชม.) — ดู calcWorkHours
  const addH = calcWorkHours(addCheckIn, addCheckOut);
  const addRate = addWorkRate;
  // ค่าแรง/วัน (8 ชม.): ใช้ค่าที่กรอกในช่อง "ค่าแรง (บาท)" ถ้าว่างให้ fallback เป็น spd ของพนักงาน
  const addDayRateOvr = parseFloat(addAmt);
  const addAutoAmt = addTarget?.spd || 0;
  const addDayRate =
    !isNaN(addDayRateOvr) && addAmt !== '' ? addDayRateOvr : addAutoAmt;
  // สูตร: (ชั่วโมงรวม × ตัวคูณอัตรา) × (ค่าแรง(บาท) / 8) — เหมือน SalaryOverrideDialog
  const addEff =
    addH > 0 ? Number((addH * addRate * (addDayRate / 8)).toFixed(2)) : 0;
  const addEx = addTarget ? getEntry(addTarget.empId, addTarget.day) : null;
  const addAllowanceAmt = addTarget?.allowancePerDay || 0;

  const otDlgH = parseFloat(addOtHours) || 0;
  const otDlgSpd = addOtTarget?.spd || 0;
  const otDlgAutoAmt =
    otDlgH > 0 ? otDlgH * (otDlgSpd / 8) * addOtRate : 0;
  const otDlgEx = addOtTarget
    ? getEntry(addOtTarget.empId, addOtTarget.day)
    : null;
  const otDlgHasEx = otDlgEx?.ot_hours != null;

  const bulkInMins = timeToMins(bulkCheckIn);
  const bulkOutMins = timeToMins(bulkCheckOut);
  const bulkH =
    bulkCheckIn && bulkCheckOut && bulkOutMins > bulkInMins
      ? (bulkOutMins - bulkInMins) / 60
      : 0;
  const bulkEmpCount = timestamp?.rows?.length || 0;

  // ── no-op sticky helpers (V1 layout, no sticky columns) ────────────────
  const stickyRight = () => ({});
  const stickyRightTh = () => ({});
  const stickyLeft = () => ({});

  const globalFieldRate =
    timestamp?.company_setting?.payroll?.field_allowance_rate || 200;

  return (
    <div style={readOnly ? { opacity: 0.6, filter: 'saturate(0.7)' } : undefined}>
      <SalaryOverrideDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        detailData={detailData}
        editAmtManual={editAmtManual}
        setEditAmtManual={setEditAmtManual}
        detailRate={detailRate}
        setDetailRate={setDetailRate}
        detailHours={detailHours}
        forceFullHours={forceFullHours}
        setForceFullHours={setForceFullHours}
        saving={saving}
        skipLateSaving={skipLateSaving}
        checkTimeSaving={checkTimeSaving}
        editCheckIn={editCheckIn}
        setEditCheckIn={setEditCheckIn}
        editCheckOut={editCheckOut}
        setEditCheckOut={setEditCheckOut}
        editProjectId={editProjectId}
        setEditProjectId={setEditProjectId}
        dlgFieldAllow={dlgFieldAllow}
        attendanceRules={attendanceRules}
        salaryMode={salaryMode}
        hrMgmt={hrMgmt}
        projectList={projectList}
        getEntry={getEntry}
        onSalaryOverride={onSalaryOverride}
        onSkipLateDeduct={onSkipLateDeduct}
        onCheckInTimeClear={onCheckInTimeClear}
        onProjectClear={onProjectClear}
        onHourOverride={onHourOverride}
        onOtOverride={onOtOverride}
        onManualDelete={onManualDelete}
        saveOverride={saveOverride}
        clearOverride={clearOverride}
        saveSkipLate={saveSkipLate}
        clearTimeOverride={clearTimeOverride}
        clearProjectOverride={clearProjectOverride}
        deleteWorkHours={deleteWorkHours}
        restoreWorkHours={restoreWorkHours}
        clearForceFullHours={clearForceFullHours}
        deleteOtHours={deleteOtHours}
        restoreOtHours={restoreOtHours}
        deleteManualFromDialog={deleteManualFromDialog}
        saveCheckInTime={saveCheckInTime}
        saveProject={saveProject}
        saveFieldAllowDay={saveFieldAllowDay}
      />

      <AddHoursDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        addTarget={addTarget}
        month={month}
        year={year}
        addCheckIn={addCheckIn}
        setAddCheckIn={setAddCheckIn}
        addCheckOut={addCheckOut}
        setAddCheckOut={setAddCheckOut}
        addH={addH}
        addAmt={addAmt}
        setAddAmt={setAddAmt}
        addWorkRate={addWorkRate}
        setAddWorkRate={setAddWorkRate}
        addAutoAmt={addAutoAmt}
        addAllowanceAmt={addAllowanceAmt}
        addIncludeAllowance={addIncludeAllowance}
        setAddIncludeAllowance={setAddIncludeAllowance}
        addEff={addEff}
        addEx={addEx}
        addSaving={addSaving}
        deleteManual={deleteManual}
        saveAddHours={saveAddHours}
        projectList={projectList}
        addProjectId={addProjectId}
        setAddProjectId={setAddProjectId}
      />

      <BulkDayDialog
        open={bulkDayOpen}
        onClose={() => setBulkDayOpen(false)}
        bulkDay={bulkDay}
        month={month}
        year={year}
        bulkEmpCount={bulkEmpCount}
        bulkTab={bulkTab}
        setBulkTab={setBulkTab}
        bulkCheckIn={bulkCheckIn}
        setBulkCheckIn={setBulkCheckIn}
        bulkCheckOut={bulkCheckOut}
        setBulkCheckOut={setBulkCheckOut}
        bulkH={bulkH}
        bulkRate={bulkRate}
        setBulkRate={setBulkRate}
        bulkSaving={bulkSaving}
        saveBulkDay={saveBulkDay}
        saveBulkDayRate={saveBulkDayRate}
      />

      <AddOtDialog
        open={addOtOpen}
        onClose={() => setAddOtOpen(false)}
        addOtTarget={addOtTarget}
        month={month}
        year={year}
        addOtRate={addOtRate}
        setAddOtRate={setAddOtRate}
        addOtHours={addOtHours}
        setAddOtHours={setAddOtHours}
        otDlgAutoAmt={otDlgAutoAmt}
        otDlgEx={otDlgEx}
        otDlgHasEx={otDlgHasEx}
        addOtSaving={addOtSaving}
        saveAddOt={saveAddOt}
        clearManualOt={clearManualOt}
      />

      <NoHoursWarningDialog
        open={noHoursWarnDay != null}
        onClose={() => setNoHoursWarnDay(null)}
        day={noHoursWarnDay}
        month={month}
        year={year}
      />

      <DayDetailView
        open={dvOpen}
        onClose={() => setDvOpen(false)}
        dvData={dvData}
        attendanceRules={attendanceRules}
        month={month}
        year={year}
        hrMgmt={hrMgmt}
        onSkipLateDeduct={onSkipLateDeduct}
        skipLateDvSaving={skipLateDvSaving}
        saveSkipLateDv={saveSkipLateDv}
      />

      {readOnly && (
        <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 6, background: '#fef3c7', borderBottom: '1px solid #fde68a', color: '#92400e', fontSize: '0.8125rem', fontWeight: 600 }}>
          🔒 งวดนี้บันทึกเงินเดือนไปแล้ว — ดูได้อย่างเดียว ไม่สามารถแก้ไขหรือบันทึกซ้ำได้
        </div>
      )}

      {typeSelect === 'FULLTIME' && hrMgmt && !readOnly && (
        <div style={{ padding: '6px 8px', display: 'flex', justifyContent: 'flex-end', borderBottom: '1px solid #e5e7eb' }}>
          <button disabled={fillSaving} onClick={fillSundays}
            style={{ fontSize: '0.75rem', color: '#7c3aed', border: '1px solid #c4b5fd', borderRadius: 4, background: '#fff', padding: '4px 12px', cursor: 'pointer', opacity: fillSaving ? 0.7 : 1 }}>
            {fillSaving ? 'กำลังเติม...' : 'เติม 8 ชม. วันอาทิตย์ทั้งเดือน'}
          </button>
        </div>
      )}

      {/* ── Loading overlay ── */}
      {isLoading && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '32px 40px', minWidth: 280, textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.45)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '1.1rem', marginBottom: 4 }}>กำลังโหลดข้อมูล</div>
            <div style={{ color: '#64748b', fontSize: '0.875rem' }}>กรุณารอสักครู่...</div>
          </div>
        </div>
      )}

      <div
        ref={scrollRef}
        style={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 260px)',
          WebkitOverflowScrolling: 'touch',
          borderRadius: '8px',
          background: '#FFFFFF',
          border: `1px solid ${BG.border}`,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.06)',
          cursor: grabbing ? 'grabbing' : 'grab',
          userSelect: 'none',
          contain: 'layout',
        }}
        onScroll={handleScroll}
        onMouseDown={onScrollDown}
        onMouseMove={onScrollMove}
        onMouseUp={onScrollUp}
        onMouseLeave={onScrollUp}
        onClickCapture={onScrollClickCapture}
      >
        <table
          style={{
            borderCollapse: 'collapse',
            fontSize: '13px',
            fontFamily: 'inherit',
            whiteSpace: 'nowrap',
            width: '100%',
            minWidth: 480,
            background: '#FFFFFF',
            tableLayout: 'auto',
            color: BG.text,
          }}
        >
          <PayrollTableHeader
            days={days}
            dayMeta={dayMeta}
            month={month}
            year={year}
            hrMgmt={hrMgmt}
            salaryMode={salaryMode}
            openBulkDay={openBulkDay}
          />
          <tbody>
            {spacerTop > 0 && (
              <tr aria-hidden="true"><td colSpan={9999} style={{ height: spacerTop, padding: 0, border: 'none' }} /></tr>
            )}
            {(timestamp?.rows || []).slice(rowStart, rowEnd).map((emp, localIdx) => {
              const idx = rowStart + localIdx;
              const isEven = idx % 2 === 0;
              const rowBg = isEven ? BG.rowEven : BG.rowOdd;
              const empId = String(emp.employee_id);
              const fi = fieldIndexMap[empId];
              const ff = fi !== undefined ? fields[fi] : null;
              const faInfo = faBreakdownMap[empId];
              const faEnabled = faInfo?.faEnabled || false;
              const faAmt = faInfo?.faDay ?? globalFieldRate;

              // ── manual_fields helpers (per-row) ──────────────────────────
              const _markField = (fieldName) => {
                if (fi === undefined) return;
                const mf = getValues(`salaryData.${fi}.manual_fields`) || [];
                if (!mf.includes(fieldName))
                  setValue(`salaryData.${fi}.manual_fields`, [
                    ...mf,
                    fieldName,
                  ]);
              };
              const _unmarkField = (fieldName) => {
                if (fi === undefined) return;
                const mf = getValues(`salaryData.${fi}.manual_fields`) || [];
                setValue(
                  `salaryData.${fi}.manual_fields`,
                  mf.filter((f) => f !== fieldName),
                );
              };
              const _applyMarkUnmark = (rawValue, formPath, fieldName) => {
                const autoVal = autoValuesRef.current[empId]?.[fieldName] ?? 0;
                const isEmpty =
                  rawValue === '' ||
                  rawValue === null ||
                  rawValue === undefined;
                if (isEmpty) {
                  if (fi !== undefined)
                    setValue(`salaryData.${fi}.${formPath}`, autoVal);
                  _unmarkField(fieldName);
                } else {
                  const numVal = parseFloat(rawValue);
                  // ถ้าพิมพ์ค่าเดียวกับ auto → ถือว่าไม่ได้แก้จริง → unmark
                  if (!isNaN(numVal) && Math.abs(numVal - autoVal) < 0.005) {
                    _unmarkField(fieldName);
                  } else {
                    _markField(fieldName);
                  }
                }
              };
              const _handleChange = (e, cf, timerKey, fieldName, formPath) => {
                cf.onChange(e);
                const rawValue = e.target.value;
                lastRawValueRef.current[timerKey] = rawValue; // เก็บค่าล่าสุดจาก onChange
                dirtyFieldsRef.current[timerKey] = true;
                clearTimeout(adjDebounceRef.current[timerKey]);
                adjDebounceRef.current[timerKey] = setTimeout(() => {
                  // ไม่ reset dirty ที่นี่ — ให้ blur เป็นคนรับผิดชอบ reset เพียงที่เดียว
                  // ถ้า reset dirty ใน debounce: type → debounce fires → dirty=false → ลบ → blur → wasDirty=false → unmark ไม่ถูกเรียก (bug!)
                  _applyMarkUnmark(rawValue, formPath, fieldName);
                  onAdjSave?.(empId);
                }, 800);
              };
              const _handleBlur = (e, cf, formPath, fieldName, timerKey) => {
                let rawValue =
                  lastRawValueRef.current[timerKey] ?? e.target.value;

                if (rawValue !== '' && rawValue != null) {
                  const num = Number(rawValue);
                  if (!isNaN(num)) {
                    rawValue = num.toFixed(2);
                    if (fi !== undefined) {
                      setValue(`salaryData.${fi}.${formPath}`, rawValue);
                    }
                  }
                }

                cf.onBlur(e);
                clearTimeout(adjDebounceRef.current[timerKey]);
                const wasDirty = dirtyFieldsRef.current[timerKey];
                dirtyFieldsRef.current[timerKey] = false;
                if (!wasDirty) return;

                _applyMarkUnmark(rawValue, formPath, fieldName);
                onAdjSave?.(empId);
              };

              // ดึงจาก pre-computed map (คำนวณไว้แล้วใน empSummaryMap)
              const empSum = empSummaryMap[empId] || {};
              const {
                displayCheck,
                displayCheckEff,
                displayAmt,
                manualAmt = 0,
                netOtH: displayNetOtH,
                netOtEffH: displayNetOtEffH,
              } = empSum;

              // ── Breakdown popover builders (lazy — computed only on click, not every render) ─
              const hrR = (emp.salary_per_day || 0) / 8;

              const buildSalaryContent = !showFormulas ? null : () => {
                let salaryBreakdownTotal = 0;
                const isFulltimeEmp = emp.emp_type === 'FULLTIME';
                // deduplicate: keep only one checkin per calendar date (last one wins, same as checkinByKey)
                const seenDates = new Set();
                const uniqueCheckins = [];
                for (const ck of [...(emp.checkins || [])].reverse()) {
                  const dk = dayjs(ck.checkIn).format('YYYY-MM-DD');
                  if (!seenDates.has(dk)) {
                    seenDates.add(dk);
                    uniqueCheckins.unshift(ck);
                  }
                }
                const salaryRows = [
                  ...uniqueCheckins
                    .map((ck) => {
                      const d2 = dayjs(ck.checkIn);
                      const ckDay = d2.date();
                      const lv = isFulltimeEmp
                        ? leaveByKey[`${empId}-${ckDay}`]
                        : null;
                      const r = calcCheckinDisplaySalary(
                        ck,
                        lv,
                        emp.salary_per_day || 0,
                      );
                      if (!r) return null; // leave ครอบ — ไม่นับ checkin
                      const lbl = `${ckDay}/${month + 1} (${
                        DAY_TH_ABBR[d2.day()]
                      })`;
                      salaryBreakdownTotal += r.amt;
                      if (r.isOverride) {
                        const sub = `${fmt(r.hours, 2)}h × ×${
                          r.rate
                        } × ฿${fmt(r.dayRate, 0)}/วัน (${fmt(
                          r.dayRate / 8,
                        )}/ชม.)`;
                        return bRow(
                          lbl,
                          sub,
                          r.amt,
                          `✎ ${ck.salary_override.edited_by || 'HR'}`,
                          '#f59e0b',
                        );
                      }
                      const sub = `${r.hours}h × ×${r.rate} × ฿${fmt(
                        emp.salary_per_day,
                        0,
                      )}/วัน (${fmt(hrR)}/ชม.)`;
                      return bRow(
                        lbl,
                        sub,
                        r.amt,
                        r.rate > 1 ? `[×${r.rate} วันหยุด]` : null,
                        '#10b981',
                      );
                    })
                    .filter(Boolean),
                  // leave rows (FULLTIME only)
                  ...(!isFulltimeEmp
                    ? []
                    : days.reduce((acc, d2) => {
                        const lv = leaveByKey[`${empId}-${d2}`];
                        if (!lv) return acc;
                        const lvH = lv.period === 'FULL_DAY' ? 8 : 4;
                        const lvAmt = (lvH / 8) * (emp.salary_per_day || 0);
                        const d2js = dayjs(
                          `${year}-${String(month + 1).padStart(
                            2,
                            '0',
                          )}-${String(d2).padStart(2, '0')}`,
                        );
                        const lbl = `${d2}/${month + 1} (${
                          DAY_TH_ABBR[d2js.day()]
                        })`;
                        salaryBreakdownTotal += lvAmt;
                        acc.push(
                          bRow(
                            lbl,
                            `${lvH}h (ลา${
                              lv.period === 'FULL_DAY' ? 'เต็มวัน' : 'ครึ่งวัน'
                            }) × ฿${fmt(emp.salary_per_day, 0)}/วัน`,
                            lvAmt,
                            'ลา',
                            '#6366f1',
                          ),
                        );
                        return acc;
                      }, [])),
                  ...days.reduce((acc, d2) => {
                    const e2 = manualByKey[`${empId}-${d2}`];
                    if (!e2 || !e2.hours || e2.timestamp_ref) return acc;
                    const amt2 =
                      e2.amount != null
                        ? e2.amount
                        : (e2.hours / 8) * (emp.salary_per_day || 0);
                    salaryBreakdownTotal += amt2;
                    acc.push(
                      bRow(
                        `${d2}/${month + 1} (manual)`,
                        `${e2.hours}h ÷ 8 × ${fmt(emp.salary_per_day)}`,
                        amt2,
                        '✎ manual',
                        '#3b82f6',
                      ),
                    );
                    return acc;
                  }, []),
                ];
                return (
                  <Box sx={{ minWidth: 250 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#94a3b8', display: 'block', mb: 0.75 }}
                    >
                      hrRate = {fmt(emp.salary_per_day)} ÷ 8 = {fmt(hrR)}
                    </Typography>
                    {salaryRows}
                    <Box
                      sx={{
                        borderTop: '1px solid #e5e7eb',
                        mt: 0.75,
                        pt: 0.75,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        รวม
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: '#065f46' }}
                      >
                        {fmt(salaryBreakdownTotal)}
                      </Typography>
                    </Box>
                  </Box>
                );
              };

              const buildOtContent = !showFormulas ? null : () => {
                // ใช้ otBreakdownMap ที่คำนวณครั้งเดียวไว้แล้ว (ตรงกับคอลัมน์ OT เป๊ะๆ) — ที่นี่แค่ขึ้นรูปเป็น JSX
                const { total: otBreakdownTotal, rows: otRows } =
                  otBreakdownMap[empId] || { total: 0, rows: [] };
                const otBdRows = otRows.map((r) => {
                  const daySalary = r.dayHrR * 8;
                  if (r.isManualOt && !r.isOverride) {
                    return bRow(
                      `${r.day}/${month + 1} (+OT)`,
                      `${r.otHours}h × ×${r.otRate} × ฿${fmt(r.dayHrR)}/ชม.`,
                      r.otAmt,
                      '✎ เพิ่มเอง',
                      '#3b82f6',
                    );
                  }
                  const dj = dayjs(r.checkin.checkIn);
                  const lbl = `${dj.date()}/${month + 1} (${
                    DAY_TH_ABBR[dj.day()]
                  })`;
                  if (r.isOverride) {
                    return bRow(
                      lbl,
                      `${r.otHours}h × ×${r.otRate} × ฿${fmt(
                        r.dayHrR,
                      )}/ชม.  |  ค่าแรงวัน ฿${fmt(daySalary, 0)}`,
                      r.otAmt,
                      `✎ override (แทน ${r.approvedHours}h ×${r.approvedRate})`,
                      '#f59e0b',
                    );
                  }
                  return bRow(
                    lbl,
                    `${r.otHours}h × ×${r.otRate} × ฿${fmt(
                      r.dayHrR,
                    )}/ชม.  |  ค่าแรงวัน ฿${fmt(daySalary, 0)}`,
                    r.otAmt,
                    '[อนุมัติ]',
                    '#10b981',
                  );
                });
                return (
                  <Box sx={{ minWidth: 250 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#94a3b8', display: 'block', mb: 0.75 }}
                    >
                      OT = ชม. × ×rate × (ค่าแรงวันนั้น ÷ 8)
                    </Typography>
                    {otBdRows.length === 0 ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        ไม่มี OT
                      </Typography>
                    ) : (
                      otBdRows
                    )}
                    <Box
                      sx={{
                        borderTop: '1px solid #e5e7eb',
                        mt: 0.75,
                        pt: 0.75,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        รวม
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: '#92400e' }}
                      >
                        {fmt(otBreakdownTotal)}
                      </Typography>
                    </Box>
                  </Box>
                );
              };

              const buildAllowanceContent = !showFormulas ? null : () => {
                // อัตราเบี้ยเลี้ยงต่อวัน — ดูจาก checkin ที่มี extra_day ก่อน ถ้าไม่มีดู manual entry
                const rateFromCheckin =
                  (emp.checkins || []).find((c) => (c.extra_day || 0) > 0)
                    ?.extra_day || 0;
                const rateFromManual =
                  days
                    .map((d) => getEntry(empId, d)?.allowance_amount || 0)
                    .find((a) => a > 0) || 0;
                const dailyRate = rateFromCheckin || rateFromManual;

                let allowTotal = 0;
                const allowRows = [];

                days.forEach((d) => {
                  const ck = checkinByKey[`${empId}-${d}`];
                  const entry = getEntry(empId, d);
                  const hasWork =
                    ck || (entry?.hours > 0 && !entry?.timestamp_ref);
                  if (!hasWork) return;

                  // ใช้ rate จาก checkin/entry วันนั้น ถ้ามี ไม่งั้นใช้ dailyRate
                  const amt =
                    ck?.extra_day || entry?.allowance_amount || dailyRate;
                  if (!amt) return;

                  const dt = dayjs(
                    `${year}-${String(month + 1).padStart(2, '0')}-${String(
                      d,
                    ).padStart(2, '0')}`,
                  );
                  const lbl = `${d}/${month + 1} (${DAY_TH_ABBR[dt.day()]})`;
                  const src = entry?.allowance_amount ? 'manual' : 'checkin';
                  allowTotal += amt;
                  allowRows.push(
                    bRow(
                      lbl,
                      `฿${fmt(amt)} / วัน (${src})`,
                      amt,
                      null,
                      '#0ea5e9',
                    ),
                  );
                });

                return (
                  <Box sx={{ minWidth: 240 }}>
                    {dailyRate > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: '#94a3b8', display: 'block', mb: 0.75 }}
                      >
                        {`อัตรา ฿${fmt(dailyRate)}/วันทำงาน`}
                      </Typography>
                    )}
                    {allowRows.length === 0 ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        ไม่มีรายการเบี้ยเลี้ยง
                      </Typography>
                    ) : (
                      allowRows
                    )}
                    {allowRows.length > 0 && (
                      <Box
                        sx={{
                          borderTop: '1px solid #e5e7eb',
                          mt: 0.75,
                          pt: 0.75,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant="caption" fontWeight={700}>
                          รวม {allowRows.length} วัน
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: '#0369a1' }}
                        >
                          +{fmt(allowTotal)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              };

              const buildLateContent = !showFormulas ? null : () => {
                const W_START_L = 8 * 60;
                const lateRule = (attendanceRules || []).find(
                  (r) => r.type === 'LATE_ARRIVAL' && r.enabled,
                );
                const lateSpm = emp.salary_per_month || 0;
                const lateSrpd = emp.salary_per_day || 0;
                let lateBreakdownTotal = 0;
                const uniqueCk = [];
                const seen = new Set();
                [...(emp.checkins || [])].reverse().forEach((ck) => {
                  const dk = dayjs(ck.checkIn).format('YYYY-MM-DD');
                  if (!seen.has(dk)) {
                    seen.add(dk);
                    uniqueCk.unshift(ck);
                  }
                });
                const lateBdRows = uniqueCk.flatMap((ck) => {
                  if (ck.is_manual) return [];
                  if (ck.skip_late_deduct?.enabled) return [];
                  if (ck.hour_override?.hours != null) return [];
                  const cit = ck.checkInTime;
                  if (!cit) return [];
                  const [citH, citM] = cit.split(':').map(Number);
                  const isAfternoon = citH >= 12;
                  const shiftStartL = isAfternoon ? 13 * 60 : W_START_L;
                  const ciMin = citH * 60 + citM;
                  const lateMin = Math.max(0, ciMin - shiftStartL);
                  if (!lateMin) return [];
                  let effectiveMin;
                  if (!lateRule) {
                    effectiveMin = lateMin;
                  } else {
                    if (lateMin < lateRule.threshold_minutes) return [];
                    effectiveMin = Math.max(
                      lateMin,
                      lateRule.deduct_minutes || 0,
                    );
                  }
                  const baseDr =
                    ck.salary_override?.day_rate ??
                    (lateSrpd > 0 ? lateSrpd : lateSpm / 30);
                  const ckRate =
                    ck.salary_override?.rate ??
                    ck.effectiveRate ??
                    ck.rate ??
                    1;
                  const effectiveDr = baseDr * ckRate;
                  const ckMinRate = effectiveDr / 8 / 60;
                  const ckHrRate = ckMinRate * 60;
                  const deductAmt = Number(
                    (effectiveMin * ckMinRate).toFixed(2),
                  );
                  lateBreakdownTotal += deductAmt;
                  const ci = dayjs(ck.checkIn);
                  const shiftLabel = isAfternoon ? '13:00' : '08:00';
                  const lbl = `${ci.date()}/${month + 1} (${
                    DAY_TH_ABBR[ci.day()]
                  }) [${shiftLabel}] (สาย ${lateMin} นาที) (฿${fmt(
                    effectiveDr,
                    0,
                  )}/วัน)`;
                  const ruleNote = lateRule
                    ? `เข้า ${cit} [${shiftLabel}] → max(${lateMin}, ${lateRule.deduct_minutes}) = ${effectiveMin} นาที`
                    : `เข้า ${cit} [${shiftLabel}]`;
                  const sub = `฿${fmt(ckHrRate)}/ชม. , ฿${fmt(
                    ckMinRate,
                    4,
                  )}/นาที\n= ${effectiveMin} × ${fmt(ckMinRate, 4)}/นาที`;
                  return [bRow(lbl, sub, deductAmt, ruleNote, '#f97316')];
                });
                return (
                  <Box sx={{ minWidth: 260 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#94a3b8', display: 'block', mb: 0.75 }}
                    >
                      {lateRule
                        ? `กฎ: สาย ≥ ${lateRule.threshold_minutes} นาที → หัก ${lateRule.deduct_minutes} นาที (เช้า 08:00 / บ่าย 13:00)`
                        : 'ไม่มีกฎ: คำนวณนาทีสาย (เช้า 08:00 / บ่าย 13:00)'}
                    </Typography>
                    {lateBdRows.length === 0 ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        ไม่มีรายการหักสาย
                      </Typography>
                    ) : (
                      lateBdRows
                    )}
                    {lateBdRows.length > 0 && (
                      <Box
                        sx={{
                          borderTop: '1px solid #e5e7eb',
                          mt: 0.75,
                          pt: 0.75,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant="caption" fontWeight={700}>
                          รวมหักสาย
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: '#ea580c' }}
                        >
                          −{fmt(lateBreakdownTotal)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              };

              // ── Build dailyRows สำหรับส่งให้ modal รายงานค่าแรง ──
              // ใช้ logic เดียวกับ buildSalaryContent + buildOtContent + buildLateContent + buildAllowanceContent
              const buildDailyRowsForReport = () => {
                const spd2 = emp.salary_per_day || 0;
                const isFulltime = emp.emp_type === 'FULLTIME';
                const lateRule2 = (attendanceRules || []).find(
                  (r) => r.type === 'LATE_ARRIVAL' && r.enabled,
                );

                const getLeaveAutoH2 = (d, ck) => {
                  const lv = leaveByKey[`${empId}-${d}`];
                  if (!lv || !isFulltime) return 0;
                  const ch = ck ? dayjs(ck.checkIn).hour() : null;
                  if (lv.period === 'FULL_DAY' && !ck) return 8;
                  if (lv.period === 'MORNING' && (!ck || ch >= 12)) return 4;
                  if (lv.period === 'AFTERNOON' && (!ck || ch < 12)) return 4;
                  return 0;
                };

                const calcLateDed2 = (ck) => {
                  if (
                    !ck ||
                    ck.is_manual ||
                    ck.skip_late_deduct?.enabled ||
                    ck.hour_override?.hours != null
                  )
                    return 0;
                  const cit = ck.checkInTime;
                  if (!cit) return 0;
                  const [citH, citM] = cit.split(':').map(Number);
                  const shiftStart2 = citH >= 12 ? 13 * 60 : 8 * 60;
                  const lateMin = Math.max(0, citH * 60 + citM - shiftStart2);
                  if (!lateMin) return 0;
                  if (lateRule2 && lateMin < lateRule2.threshold_minutes)
                    return 0;
                  const effectiveMin = lateRule2
                    ? Math.max(lateMin, lateRule2.deduct_minutes || 0)
                    : lateMin;
                  const baseDr = ck.salary_override?.day_rate ?? spd2;
                  const ckRate =
                    ck.salary_override?.rate ??
                    ck.effectiveRate ??
                    ck.rate ??
                    1;
                  return Number(
                    ((effectiveMin * baseDr * ckRate) / 8 / 60).toFixed(2),
                  );
                };

                const rows = [];
                for (const d of days) {
                  const ck = checkinByKey[`${empId}-${d}`] || null;
                  const mn = manualByKey[`${empId}-${d}`] || null;
                  const leave = leaveByKey[`${empId}-${d}`] || null;
                  const leaveAutoH = getLeaveAutoH2(d, ck);
                  const date = dayjs(
                    `${year}-${String(month + 1).padStart(2, '0')}-${String(
                      d,
                    ).padStart(2, '0')}`,
                  );
                  const dow = date.day();
                  const isSun = dow === 0;
                  const isSat = dow === 6;

                  const validManual = mn && !mn.timestamp_ref ? mn : null;
                  if (!ck && !validManual && leaveAutoH === 0) {
                    rows.push({
                      d,
                      dow,
                      date,
                      checkInTime: null,
                      checkOutTime: null,
                      hours: 0,
                      wage: 0,
                      actualH: 0,
                      actualWage: 0,
                      allowance: 0,
                      fieldAllowance: 0,
                      notes: [],
                      er: 1,
                      otHours: 0,
                      otRate: 1.5,
                      otAmt: 0,
                      lateDed: 0,
                      leaveAutoH: 0,
                      isSun,
                      isSat,
                      isManual: false,
                      isLeaveOnly: false,
                      isEmpty: true,
                      project: null,
                    });
                    continue;
                  }

                  const er = ck
                    ? ck.is_manual
                      ? 1
                      : ck.effectiveRate || ck.rate || 1
                    : validManual?.work_rate || 1;
                  let wage = 0;
                  let hours = 0;
                  let origDayWage = null;
                  let actualH = 0;
                  let actualWage = 0;
                  const notes = [];

                  // salary — calcCheckinDisplaySalary/calcCheckinActualSalary (ตรงกับ column + popup เป๊ะๆ)
                  // ฟังก์ชันนี้จัดการ leave แยกต่างหากด้านล่าง (leaveAutoH) จึงส่ง lv=null เสมอ (ไม่ cap/skip ในนี้)
                  if (ck) {
                    const citH4 = ck.checkInTime
                      ? parseInt(ck.checkInTime.split(':')[0], 10)
                      : 0;
                    const isAft4 = citH4 >= 12;
                    const actualR = calcCheckinActualSalary(ck, null, spd2);
                    actualH = actualR.hours;
                    actualWage = actualR.amt;

                    const displayR = calcCheckinDisplaySalary(ck, null, spd2);
                    wage = displayR.amt;
                    hours = displayR.hours;
                    if (displayR.isOverride) {
                      origDayWage = displayR.defaultAmt;
                      notes.push({
                        text: `แก้ค่าแรงโดย: ${
                          ck.salary_override.edited_by || 'HR'
                        }`,
                        type: 'wage',
                      });
                    } else {
                      if (er > 1)
                        notes.push({ text: `×${er} วันหยุด`, type: 'holiday' });
                      if (isAft4)
                        notes.push({
                          text: 'ลงเวลาช่วงบ่าย',
                          type: 'afternoon',
                        });
                    }
                    if (
                      ck.deductionReasons?.includes('late') &&
                      !ck.skip_late_deduct?.enabled
                    )
                      notes.push({ text: 'สาย ส', type: 'late' });
                    if (ck.deductionReasons?.includes('no_checkout'))
                      notes.push({
                        text: 'ไม่เช็คเอาท์ อ',
                        type: 'nocheckout',
                      });
                    if (ck.skip_late_deduct?.enabled)
                      notes.push({ text: 'ยกเว้นหักสาย', type: 'skip_late' });
                  }
                  if (validManual) {
                    const mRate = validManual.work_rate || 1;
                    const mWage =
                      validManual.amount != null
                        ? Number(validManual.amount)
                        : (validManual.hours / 8) * spd2 * mRate;
                    const rateLabel = mRate > 1 ? ` (×${mRate} วันหยุด)` : '';
                    if (!ck) {
                      hours = validManual.hours || 0;
                      wage = mWage;
                      actualH = validManual.hours || 0;
                      actualWage = mWage;
                      notes.push({
                        text: `เพิ่มชั่วโมง ${
                          validManual.hours
                        } ชม.${rateLabel} ค่าแรง ${fmt(mWage)} บาท โดย ${
                          validManual.edited_by || 'HR'
                        }`,
                        type: 'hour',
                      });
                    } else {
                      wage += mWage;
                      actualWage += mWage;
                      notes.push({
                        text: `+เพิ่มชั่วโมง ${
                          validManual.hours
                        } ชม.${rateLabel} +${fmt(mWage)} บาท โดย ${
                          validManual.edited_by || 'HR'
                        }`,
                        type: 'hour',
                      });
                    }
                  }
                  if (leaveAutoH > 0) {
                    const leaveWage = (leaveAutoH / 8) * spd2;
                    const leaveName =
                      LEAVE_ABBR[leave?.leave_type] ||
                      leave?.leave_type ||
                      'ลา';
                    const pLbl =
                      leave?.period === 'FULL_DAY'
                        ? 'เต็มวัน'
                        : leave?.period === 'MORNING'
                        ? 'ครึ่งวันเช้า'
                        : 'ครึ่งวันบ่าย';
                    hours += leaveAutoH;
                    wage += leaveWage;
                    actualH += leaveAutoH;
                    actualWage += leaveWage;
                    notes.push({
                      text: `ลา${leaveName} (${pLbl}) +${leaveAutoH} ชม. = +${fmt(
                        leaveWage,
                      )} บาท`,
                      type: 'leave',
                    });
                  }

                  // OT — ดึงจาก otBreakdownMap ที่คำนวณครั้งเดียวไว้แล้ว (ตรงกับ column OT + popup สูตร OT เป๊ะๆ)
                  const otDay = otBreakdownMap[empId]?.byDay?.[d];
                  const otHours = otDay?.otHours || 0;
                  const otRate = otDay?.otRate ?? 1.5;
                  const otAmt = otDay?.otAmt || 0;
                  const isManualOt = otDay?.isManualOt || false;

                  // allowance + late (buildAllowanceContent / buildLateContent)
                  const allowance = ck?.extra_day || mn?.allowance_amount || 0;
                  const lateDed = calcLateDed2(ck);

                  // field allowance — ดึงจาก faBreakdownMap ที่คำนวณครั้งเดียวไว้แล้ว (ตรงกับ column + popup เป๊ะๆ)
                  const fieldAllowance =
                    faBreakdownMap[empId]?.byDay?.[d]?.amt || 0;

                  rows.push({
                    d,
                    dow,
                    date,
                    checkInTime: ck?.checkInTime || null,
                    checkOutTime: ck?.checkOutTime || null,
                    project: ck?.project_name
                      ? `${ck.project_name}${
                          ck.project_number ? ` (${ck.project_number})` : ''
                        }`
                      : null,
                    projectNumber: ck?.project_number || null,
                    projectId: ck?.project_id ? String(ck.project_id) : null,
                    hours,
                    wage,
                    origDayWage,
                    actualH,
                    actualWage,
                    allowance,
                    fieldAllowance,
                    notes,
                    er,
                    otHours,
                    otRate,
                    otAmt,
                    isManualOt,
                    lateDed,
                    leaveAutoH,
                    leave,
                    isSun,
                    isSat,
                    isManual: !ck && !!validManual,
                    isLeaveOnly: !ck && !validManual && leaveAutoH > 0,
                    isEmpty: false,
                    isHourForced: ck?.hour_override?.hours === 8,
                    rawHoursBeforeForce: ck?.hour_override?.previous_hours ?? null,
                  });
                }
                return rows;
              };

              const buildSsoContent = !showFormulas ? null : () => {
                const ssoEnabled = !!ff?.sso_tax?.status;
                const ssoRate = ff?.sso_tax?.number || 5;
                const ssoPaid = ff?.sso_paid || 0;
                const ssoBase = salaryMode === 'work-time' ? (empSum.actualSalaryAmt || 0) : displayAmt;
                const ssoRawCalc = ssoBase * (ssoRate / 100);
                let ssoThisPeriod = ssoRawCalc;
                if (ssoThisPeriod > 0) {
                  if (ssoThisPeriod + ssoPaid > 875)
                    ssoThisPeriod = Math.max(0, 875 - ssoPaid);
                  if (ssoThisPeriod + ssoPaid < 83)
                    ssoThisPeriod = Math.max(0, 83 - ssoPaid);
                }
                // แสดง field งวดที่ 1 เมื่อ: filter 16-30 AND พนักงานรายวัน
                const isPeriod2 = selectedOption === '31';
                const isDaily = emp.emp_type !== 'FULLTIME';
                const showPeriod1Row = isPeriod2 && isDaily;
                return (
                  <Box sx={{ minWidth: 270 }}>
                    {!ssoEnabled ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        ไม่ได้เปิดใช้ประกันสังคมสำหรับพนักงานคนนี้
                      </Typography>
                    ) : (
                      <>
                        {showPeriod1Row && (
                          <Box
                            sx={{
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: 1,
                              p: 0.75,
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#64748b',
                                fontWeight: 600,
                                display: 'block',
                                mb: 0.5,
                              }}
                            >
                              งวดที่ 1 (1–15)
                            </Typography>
                            {ssoPaid > 0 ? (
                              <>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ color: '#475569' }}
                                  >
                                    SSO บันทึกแล้ว
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ color: '#b45309' }}
                                  >
                                    ฿{fmt(ssoPaid)}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mt: 0.25,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: '0.68rem',
                                    }}
                                  >
                                    เหลือหักได้อีก
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: '0.68rem',
                                    }}
                                  >
                                    875 − {fmt(ssoPaid)} = ฿{fmt(875 - ssoPaid)}
                                  </Typography>
                                </Box>
                              </>
                            ) : (
                              <Typography
                                variant="caption"
                                sx={{ color: '#94a3b8', fontStyle: 'italic' }}
                              >
                                ยังไม่บันทึกงวดที่ 1
                              </Typography>
                            )}
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#475569' }}
                          >
                            {showPeriod1Row
                              ? 'ฐานค่าแรงงวดที่ 2 (16–สิ้นเดือน)'
                              : 'ฐานค่าแรงงวดนี้'}
                          </Typography>
                          <Typography variant="caption" fontWeight={700}>
                            ฿{fmt(ssoBase)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: showPeriod1Row || ssoPaid > 0 ? 0.5 : 0.75,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#475569' }}
                          >
                            คำนวณ {ssoRate}%
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            ฿{fmt(ssoRawCalc)}
                          </Typography>
                        </Box>
                        {!showPeriod1Row && ssoPaid > 0 && (
                          <>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: '#6b7280' }}
                              >
                                จ่ายแล้วงวดก่อน
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: '#6b7280' }}
                              >
                                +฿{fmt(ssoPaid)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mb: 0.75,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: '#475569' }}
                              >
                                รวมสะสม
                              </Typography>
                              <Typography variant="caption" fontWeight={600}>
                                ฿{fmt(ssoRawCalc + ssoPaid)}
                              </Typography>
                            </Box>
                          </>
                        )}
                        {showPeriod1Row && ssoPaid > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 0.75,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: '#475569' }}
                            >
                              รวมสะสมทั้งเดือน
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              ฿{fmt(ssoRawCalc + ssoPaid)}
                            </Typography>
                          </Box>
                        )}
                        <Box
                          sx={{
                            borderTop: '1px solid #e5e7eb',
                            pt: 0.75,
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography variant="caption" fontWeight={700}>
                            {showPeriod1Row
                              ? 'SSO หักงวดที่ 2'
                              : 'SSO หักงวดนี้'}
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ color: '#b91c1c' }}
                          >
                            −฿{fmt(ssoThisPeriod)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#94a3b8', fontSize: '0.68rem' }}
                          >
                            เกณฑ์กฎหมาย
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: '#94a3b8', fontSize: '0.68rem' }}
                          >
                            83 – 875 บาท/เดือน
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                );
              };

              const buildNetContent = !showFormulas ? null : () => {
                const rowData = getValues
                  ? getValues(`salaryData.${fi}`) || {}
                  : {};
                const sal = parseFloat(
                  rowData?.revenue?.salary ?? ff?.totalAmount ?? 0,
                );
                const ot = parseFloat(rowData?.revenue?.overtime || 0);
                const all = parseFloat(
                  rowData?.revenue?.allowances ??
                    ff?.revenue?.allowances ??
                    ff?.total_extra_day ??
                    0,
                );
                const fieldAllow = parseFloat(
                  rowData?.revenue?.field_allowance || 0,
                );
                const bon = parseFloat(rowData?.revenue?.bonus || 0);
                const tax = parseFloat(
                  rowData?.expenses?.tax ?? ff?.expenses?.tax ?? 0,
                );
                const sso = parseFloat(
                  rowData?.expenses?.sso ?? ff?.expenses?.sso ?? 0,
                );
                const late = parseFloat(
                  rowData?.expenses?.late ?? ff?.expenses?.late ?? 0,
                );
                const oth = parseFloat(
                  rowData?.expenses?.other ?? ff?.expenses?.other ?? 0,
                );
                const totalIncome = sal + ot + all + fieldAllow + bon;
                const totalDeduct = tax + sso + late + oth;
                const net = totalIncome - totalDeduct;
                const incomeRows = [
                  { label: 'ค่าแรง', value: sal },
                  ot > 0 ? { label: 'OT', value: ot } : null,
                  all > 0 ? { label: 'เบี้ยเลี้ยง', value: all } : null,
                  fieldAllow > 0
                    ? { label: 'ค่าออกสนาม', value: fieldAllow }
                    : null,
                  bon > 0 ? { label: 'เงินได้อื่นๆ', value: bon } : null,
                ].filter(Boolean);
                const deductRows = [
                  tax > 0 ? { label: 'หักภาษี', value: tax } : null,
                  sso > 0 ? { label: 'ประกันสังคม', value: sso } : null,
                  late > 0 ? { label: 'หักสาย', value: late } : null,
                  oth > 0 ? { label: 'หักอื่นๆ', value: oth } : null,
                ].filter(Boolean);
                return (
                  <Box sx={{ minWidth: 230 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748b',
                        fontWeight: 700,
                        display: 'block',
                        mb: 0.5,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      รายได้
                    </Typography>
                    {incomeRows.map(({ label, value }) => (
                      <Box
                        key={label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.25,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#475569' }}>
                          {label}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{ color: '#059669' }}
                        >
                          +{fmt(value)}
                        </Typography>
                      </Box>
                    ))}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 0.25,
                        pt: 0.25,
                        borderTop: '1px dashed #e2e8f0',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        รวมรายได้
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: '#059669' }}
                      >
                        {fmt(totalIncome)}
                      </Typography>
                    </Box>
                    {deductRows.length > 0 && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                            fontWeight: 700,
                            display: 'block',
                            mt: 1,
                            mb: 0.5,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          หัก
                        </Typography>
                        {deductRows.map(({ label, value }) => (
                          <Box
                            key={label}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 0.25,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: '#475569' }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: '#dc2626' }}
                            >
                              −{fmt(value)}
                            </Typography>
                          </Box>
                        ))}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 0.25,
                            pt: 0.25,
                            borderTop: '1px dashed #e2e8f0',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748b' }}
                          >
                            รวมหัก
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ color: '#dc2626' }}
                          >
                            −{fmt(totalDeduct)}
                          </Typography>
                        </Box>
                      </>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 0.75,
                        pt: 0.75,
                        borderTop: '2px solid #334155',
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ fontSize: '0.82rem' }}
                      >
                        สุทธิ
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{
                          color: net >= 0 ? '#059669' : '#dc2626',
                          fontSize: '0.85rem',
                        }}
                      >
                        {net >= 0 ? '' : '−'}
                        {fmt(Math.abs(net))}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 0.5,
                        background: '#f8fafc',
                        borderRadius: 0.75,
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: '#94a3b8', fontSize: '0.65rem' }}
                      >
                        {fmt(totalIncome)} − {fmt(totalDeduct)} = {fmt(net)}
                      </Typography>
                    </Box>
                  </Box>
                );
              };

              const buildActualSalaryContent = !showFormulas ? null : () => {
                let total = 0;
                const isFulltimeEmp = emp.emp_type === 'FULLTIME';
                // deduplicate: keep one checkin per date (last one wins)
                const seenAct = new Set();
                const uniqueAct = [];
                for (const ck of [...(emp.checkins || [])].reverse()) {
                  const dk = dayjs(ck.checkIn).format('YYYY-MM-DD');
                  if (!seenAct.has(dk)) {
                    seenAct.add(dk);
                    uniqueAct.unshift(ck);
                  }
                }
                const rows = [
                  ...uniqueAct
                    .map((ck) => {
                      const d2 = dayjs(ck.checkIn);
                      const ckDay = d2.date();
                      const lv = isFulltimeEmp
                        ? leaveByKey[`${empId}-${ckDay}`]
                        : null;
                      const r = calcCheckinActualSalary(
                        ck,
                        lv,
                        emp.salary_per_day || 0,
                      );
                      if (!r) return null; // leave ครอบ — ไม่นับ checkin
                      const lbl = `${ckDay}/${month + 1} (${
                        DAY_TH_ABBR[d2.day()]
                      })`;
                      const sub = `${fmt(r.hours, 2)}h × ×${r.rate} × ฿${fmt(
                        r.dayRate,
                        0,
                      )}/วัน`;
                      const hasOvr = ck.salary_override?.amount != null;
                      const note = hasOvr ? (
                        <>
                          แก้โดย {ck.salary_override.edited_by || 'HR'}{' '}
                          {Math.abs(r.defaultAmt - r.amt) > 0.01 && (
                            <>
                              <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>
                                {fmt(r.defaultAmt)}
                              </span>{' '}
                              {fmt(r.amt)}
                            </>
                          )}
                        </>
                      ) : r.rate > 1
                        ? `[×${r.rate} วันหยุด]`
                        : null;
                      const noteColor = hasOvr ? '#f59e0b' : '#10b981';
                      total += r.amt;
                      return bRow(lbl, sub, r.amt, note, noteColor);
                    })
                    .filter(Boolean),
                  // leave rows (FULLTIME only)
                  ...(!isFulltimeEmp
                    ? []
                    : days.reduce((acc, d2) => {
                        const lv = leaveByKey[`${empId}-${d2}`];
                        if (!lv) return acc;
                        const lvH = lv.period === 'FULL_DAY' ? 8 : 4;
                        const lvAmt = (lvH / 8) * (emp.salary_per_day || 0);
                        const d2js = dayjs(
                          `${year}-${String(month + 1).padStart(
                            2,
                            '0',
                          )}-${String(d2).padStart(2, '0')}`,
                        );
                        const lbl = `${d2}/${month + 1} (${
                          DAY_TH_ABBR[d2js.day()]
                        })`;
                        total += lvAmt;
                        acc.push(
                          bRow(
                            lbl,
                            `${lvH}h (ลา${
                              lv.period === 'FULL_DAY' ? 'เต็มวัน' : 'ครึ่งวัน'
                            }) × ฿${fmt(emp.salary_per_day, 0)}/วัน`,
                            lvAmt,
                            'ลา',
                            '#6366f1',
                          ),
                        );
                        return acc;
                      }, [])),
                  ...days.reduce((acc, d2) => {
                    const e2 = manualByKey[`${empId}-${d2}`];
                    if (!e2 || !e2.hours || e2.timestamp_ref) return acc;
                    const amt2 =
                      e2.amount != null
                        ? e2.amount
                        : (e2.hours / 8) * (emp.salary_per_day || 0);
                    total += amt2;
                    acc.push(
                      bRow(
                        `${d2}/${month + 1} (manual)`,
                        `${e2.hours}h ÷ 8 × ${fmt(emp.salary_per_day)}`,
                        amt2,
                        '✎ manual',
                        '#3b82f6',
                      ),
                    );
                    return acc;
                  }, []),
                ];
                return (
                  <Box sx={{ minWidth: 260 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#94a3b8', display: 'block', mb: 0.75 }}
                    >
                      ใช้ชั่วโมงจริง (ไม่ min ที่ 8h) | hrRate ={' '}
                      {fmt(emp.salary_per_day)} ÷ 8 = {fmt(hrR)}
                    </Typography>
                    {rows.length === 0 ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        ไม่มีข้อมูล
                      </Typography>
                    ) : (
                      rows
                    )}
                    <Box
                      sx={{
                        borderTop: '1px solid #e5e7eb',
                        mt: 0.75,
                        pt: 0.75,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        รวม
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: '#065f46' }}
                      >
                        {fmt(total)}
                      </Typography>
                    </Box>
                  </Box>
                );
              };

              const buildActualNetContent = !showFormulas ? null : () => {
                const rowData = getValues
                  ? getValues(`salaryData.${fi}`) || {}
                  : {};
                const actualSal = empSummaryMap[empId]?.actualSalaryAmt || 0;
                const ot = parseFloat(rowData?.revenue?.overtime || 0);
                const all = parseFloat(
                  rowData?.revenue?.allowances ??
                    ff?.revenue?.allowances ??
                    ff?.total_extra_day ??
                    0,
                );
                const fieldAllow = parseFloat(
                  rowData?.revenue?.field_allowance || 0,
                );
                const bon = parseFloat(rowData?.revenue?.bonus || 0);
                const tax = parseFloat(
                  rowData?.expenses?.tax ?? ff?.expenses?.tax ?? 0,
                );
                const sso = parseFloat(
                  rowData?.expenses?.sso ?? ff?.expenses?.sso ?? 0,
                );
                const oth = parseFloat(
                  rowData?.expenses?.other ?? ff?.expenses?.other ?? 0,
                );
                const totalIncome = actualSal + ot + all + fieldAllow + bon;
                const totalDeduct = tax + sso + oth;
                const net = totalIncome - totalDeduct;
                const incomeRows = [
                  { label: 'ค่าแรง (ชม.จริง)', value: actualSal },
                  ot > 0 ? { label: 'OT', value: ot } : null,
                  all > 0 ? { label: 'เบี้ยเลี้ยง', value: all } : null,
                  fieldAllow > 0
                    ? { label: 'ค่าออกสนาม', value: fieldAllow }
                    : null,
                  bon > 0 ? { label: 'เงินได้อื่นๆ', value: bon } : null,
                ].filter(Boolean);
                const deductRows = [
                  tax > 0 ? { label: 'หักภาษี', value: tax } : null,
                  sso > 0 ? { label: 'ประกันสังคม', value: sso } : null,
                  oth > 0 ? { label: 'หักอื่นๆ', value: oth } : null,
                ].filter(Boolean);
                return (
                  <Box sx={{ minWidth: 230 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748b',
                        fontWeight: 700,
                        display: 'block',
                        mb: 0.5,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      รายได้
                    </Typography>
                    {incomeRows.map(({ label, value }) => (
                      <Box
                        key={label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.25,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#475569' }}>
                          {label}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{ color: '#059669' }}
                        >
                          +{fmt(value)}
                        </Typography>
                      </Box>
                    ))}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 0.25,
                        pt: 0.25,
                        borderTop: '1px dashed #e2e8f0',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        รวมรายได้
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: '#059669' }}
                      >
                        {fmt(totalIncome)}
                      </Typography>
                    </Box>
                    {deductRows.length > 0 && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                            fontWeight: 700,
                            display: 'block',
                            mt: 1,
                            mb: 0.5,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          หัก (ไม่รวมหักสาย)
                        </Typography>
                        {deductRows.map(({ label, value }) => (
                          <Box
                            key={label}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 0.25,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: '#475569' }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: '#dc2626' }}
                            >
                              −{fmt(value)}
                            </Typography>
                          </Box>
                        ))}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 0.25,
                            pt: 0.25,
                            borderTop: '1px dashed #e2e8f0',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748b' }}
                          >
                            รวมหัก
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ color: '#dc2626' }}
                          >
                            −{fmt(totalDeduct)}
                          </Typography>
                        </Box>
                      </>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 0.75,
                        pt: 0.75,
                        borderTop: '2px solid #334155',
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ fontSize: '0.82rem' }}
                      >
                        ที่ต้องจ่ายพนักงาน
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{
                          color: net >= 0 ? '#059669' : '#dc2626',
                          fontSize: '0.85rem',
                        }}
                      >
                        {net >= 0 ? '' : '−'}
                        {fmt(Math.abs(net))}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 0.5,
                        background: '#f8fafc',
                        borderRadius: 0.75,
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: '#94a3b8', fontSize: '0.65rem' }}
                      >
                        {fmt(totalIncome)} − {fmt(totalDeduct)} = {fmt(net)}
                      </Typography>
                    </Box>
                  </Box>
                );
              };

              const buildFieldAllowContent = !showFormulas ? null : () => {
                // ใช้ faBreakdownMap ที่คำนวณครั้งเดียวไว้แล้ว (ตรงกับคอลัมน์ค่าออกสนามเป๊ะๆ) — ที่นี่แค่ขึ้นรูปเป็น JSX
                const faInfo2 = faBreakdownMap[empId] || {
                  total: 0,
                  rows: [],
                  faHrRate: faAmt / 8,
                };
                const { total, faHrRate } = faInfo2;
                const rows = faInfo2.rows.map((r) =>
                  bRow(
                    `${r.day}/${month + 1} (${DAY_TH_ABBR[dayjs(r.checkin.checkIn).day()]})`,
                    r.otHours > 0
                      ? `${r.faHours}h × ฿${fmt(
                          r.faHrRate,
                        )}/ชม. + OT ${fmt(r.otHours, 2)}h×${r.otRate}`
                      : `${r.faHours}h × ฿${fmt(r.faHrRate)}/ชม.`,
                    r.amt,
                    null,
                    '#15803d',
                  ),
                );
                return (
                  <Box sx={{ minWidth: 260 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#94a3b8', display: 'block', mb: 0.75 }}
                    >
                      {`อัตรา ฿${fmt(faAmt, 0)}/วัน (฿${fmt(
                        faHrRate,
                      )}/ชม.) — mark จาก dialog แต่ละวัน`}
                    </Typography>
                    {rows.length === 0 ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        ยังไม่มีวันที่ mark ค่าออกสนาม
                      </Typography>
                    ) : (
                      rows
                    )}
                    {rows.length > 0 && (
                      <Box
                        sx={{
                          borderTop: '1px solid #e5e7eb',
                          mt: 0.75,
                          pt: 0.75,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant="caption" fontWeight={700}>
                          รวม {rows.length} วัน
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: '#15803d' }}
                        >
                          +{fmt(total)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              };

              return (
                <tr
                  key={idx}
                  style={{ background: rowBg }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = BG.rowHover;
                    e.currentTarget
                      .querySelectorAll('td[data-sticky]')
                      .forEach((td) => {
                        td.style.background = BG.rowHover;
                      });
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = rowBg;
                    e.currentTarget
                      .querySelectorAll('td[data-sticky]')
                      .forEach((td) => {
                        td.style.background = rowBg;
                      });
                  }}
                >
                  {/* ── info columns (sticky left) ── */}
                  <td
                    data-sticky="true"
                    style={td0({
                      position: 'sticky',
                      left: 0,
                      zIndex: 10,
                      background: rowBg,
                      width: LW.no,
                      minWidth: LW.no,
                      color: BG.textMuted,
                      fontWeight: 500,
                    })}
                  >
                    {idx + 1}
                  </td>
                  <td
                    data-sticky="true"
                    style={td0({
                      position: 'sticky',
                      left: LW.no,
                      zIndex: 10,
                      background: rowBg,
                      textAlign: 'left',
                      paddingLeft: 12,
                      paddingRight: 8,
                      borderRight: `1px solid ${BG.border}`,
                    })}
                  >
                    <FieldAllowanceBadge faEnabled={faEnabled} faAmt={faAmt} />
                    <div
                      style={{
                        fontWeight: 600,
                        color: BG.text,
                        maxWidth: 164,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <EmployeeName
                        id={emp.employee_id}
                        name={emp.name}
                        style={{ fontWeight: 600, color: BG.text }}
                      />
                    </div>
                    {ff?.role && (
                      <div
                        style={{
                          fontSize: '11px',
                          color: BG.textMuted,
                          maxWidth: 164,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          marginTop: 1,
                        }}
                      >
                        {ff.role}
                      </div>
                    )}
                    <div
                      onClick={() =>
                        setReportEmp({
                          emp,
                          fi,
                          dailyRows: buildDailyRowsForReport(),
                        })
                      }
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        marginTop: 5,
                        cursor: 'pointer',
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        borderRadius: 6,
                        padding: '3px 8px',
                        color: '#334155',
                        fontSize: '11px',
                        fontWeight: 600,
                        userSelect: 'none',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#1e293b';
                        e.currentTarget.style.color = '#fff';
                        e.currentTarget.style.borderColor = '#1e293b';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f1f5f9';
                        e.currentTarget.style.color = '#334155';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }}
                    >
                      <AssessmentIcon style={{ fontSize: 13 }} />
                      ดูรายงานค่าแรง
                    </div>
                  </td>
                  <td
                    data-sticky="true"
                    style={td0({
                      position: 'sticky',
                      left: LW.no + LW.name,
                      zIndex: 10,
                      background: rowBg,
                      padding: '8px 6px',
                      borderRight: `1px solid ${BG.border}`,
                      minWidth: LW.type,
                    })}
                  >
                    <div
                      style={{
                        borderBottom: `1px solid ${BG.border}`,
                        padding: '4px 2px',
                        marginBottom: 4,
                        fontSize: '12px',
                        color: BG.textMuted,
                        fontWeight: 400,
                      }}
                    >
                      ปกติ
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#EA580C',
                        fontWeight: 500,
                        padding: '2px 0',
                      }}
                    >
                      OT
                    </div>
                  </td>

                  {/* ── SCROLLABLE: day cells ── */}
                  {dayMeta.map(({ day, dow, isToday: td, isHoliday: hd }) => {
                    const checkin = checkinByKey[`${empId}-${day}`] || null;
                    const leave = getLeaveForDay(empId, day);
                    const ovHours = checkin?.salary_override?.hours;
                    const ovRate = checkin?.salary_override?.rate;
                    const nh = checkin
                      ? ovHours ?? checkin.totalHour ?? 0
                      : null;
                    // "นับเป็น 8 ชม." — โชว์ชั่วโมงเดิม (ขีดฆ่า) + 8
                    const isHourForced = checkin?.hour_override?.hours === 8;
                    const rawHoursBeforeForce = checkin?.hour_override?.previous_hours;
                    const eff =
                      ovRate ?? checkin?.effectiveRate ?? checkin?.rate ?? 1;
                    // nh เป็น null เฉพาะตอนไม่มี checkin เลย — ถ้ามี checkin แต่ชม.เป็น 0 จริงๆ (เช่น เข้างานหลัง 17:00)
                    // ต้องโชว์ 0 ไม่ใช่ว่าง เพื่อให้รู้ว่ามีการลงเวลาแต่ได้ชม. 0
                    const nv = nh;
                    const ov = checkin
                      ? parseFloat(
                          checkin.otRequests?.total_hours?.toFixed(2),
                        ) || null
                      : null;
                    // ใช้ rate จาก OT request โดยตรง; fallback ไป otEffectiveHours ถ้า rate ไม่มี
                    const otReqRate = checkin?.otRequests?.rate ?? null;
                    const oeh =
                      ov != null && otReqRate != null
                        ? parseFloat((ov * otReqRate).toFixed(2))
                        : checkin?.otEffectiveHours || 0;
                    // salary_override ของ checkin ที่ is_manual=true คือยอดตอน "เพิ่มชั่วโมงการทำงาน"
                    // (ไม่ใช่การแก้ไขค่าแรงของ checkin จริง) — ต้องไม่นับเป็น isOvr ไม่งั้นจะโดนสีเหลือง
                    // ทับสีฟ้าของ "เพิ่มชั่วโมงเอง" (ดู label เดียวกันใน SalaryOverrideDialog.js: is_manual ? 'เพิ่มโดย' : 'แก้ไขโดย')
                    const isOvr =
                      (checkin?.salary_override?.amount != null &&
                        !checkin?.is_manual) ||
                      checkin?.time_override != null ||
                      // project_override.original_project_id มี default:null ใน schema — Mongoose ใส่ object
                      // นี้ให้ทุก document อัตโนมัติแม้ไม่มีใครแก้ไข ต้องเช็ค edited_by (ไม่มี default) แทน
                      checkin?.project_override?.edited_by != null;
                    const isHourOverridden =
                      checkin?.hour_override?.hours != null;
                    // เข้างานหลัง 08:00 (เฉพาะกะเช้า, กะบ่ายเริ่ม 13:00 ไม่นับสาย) — badge "ส" ที่ day cell
                    const isLateArrival = (() => {
                      if (!checkin?.checkInTime) return false;
                      const [ciH, ciM] = checkin.checkInTime
                        .split(':')
                        .map(Number);
                      return ciH < 12 && ciH * 60 + ciM > 8 * 60;
                    })();
                    const ovrTip = isOvr
                      ? checkin?.salary_override?.amount != null
                        ? `แก้ไขโดย: ${checkin.salary_override.edited_by}${
                            checkin.salary_override.edited_at
                              ? ` (${dayjs(
                                  checkin.salary_override.edited_at,
                                ).format('DD/MM/YY HH:mm')})`
                              : ''
                          }`
                        : checkin?.time_override != null
                        ? `แก้ไขเวลา โดย: ${checkin.time_override.edited_by}`
                        : `แก้ไขโปรเจค โดย: ${checkin.project_override.edited_by}`
                      : '';
                    const mh = getManualHours(empId, day);
                    const hasMn = mh > 0;
                    // checkin ที่เพิ่มโดย HR ผ่านฟอร์ม "เพิ่มรายการลงเวลา" (AddTimestampTime.js)
                    // — note ขึ้นต้นด้วย "Check in by ..." หรือ "Check in/out by ..." — ให้ถือเป็นสีเดียวกับ "เพิ่มชั่วโมงด้วยตนเอง"
                    const isHrManualCheckin = /^Check in(\/out)? by /.test(
                      checkin?.note || '',
                    );
                    const manualOtH = getManualOtHours(empId, day);
                    const manualOtRate =
                      manualOtH > 0
                        ? getEntry(empId, day)?.ot_rate || 1.5
                        : 1.5;
                    const manualOtEffH = manualOtH * manualOtRate;
                    const lc = leave
                      ? LEAVE_COLOR[leave.leave_type] || LEAVE_COLOR.PERSONAL
                      : null;
                    const isHalfLeave = leave && leave.period !== 'FULL_DAY';
                    // ช่วงที่ไม่ได้ลา (ใช้แสดงให้ HR รู้ว่าควรมีการลงเวลาช่วงไหน)
                    const nonLeavePeriod = isHalfLeave
                      ? leave.period === 'MORNING'
                        ? 'บ่าย'
                        : 'เช้า'
                      : null;
                    // ตรวจสอบว่า checkin อยู่ในช่วงลาหรือไม่ (เช้า < 12:00 / บ่าย >= 12:00)
                    const checkinHour = checkin
                      ? dayjs(checkin.checkIn).hour()
                      : null;
                    const isCheckinInLeavePeriod =
                      isHalfLeave && checkin != null
                        ? leave.period === 'MORNING'
                          ? checkinHour < 12
                          : checkinHour >= 12
                        : null;
                    // ชั่วโมงลา auto สำหรับ FULLTIME: เต็มวัน=8h, ครึ่งวัน=4h
                    const leaveAutoH = (() => {
                      if (!leave || emp.emp_type !== 'FULLTIME') return 0;
                      if (leave.period === 'FULL_DAY' && !checkin) return 8;
                      if (
                        leave.period === 'MORNING' &&
                        (!checkin || checkinHour >= 12)
                      )
                        return 4;
                      if (
                        leave.period === 'AFTERNOON' &&
                        (!checkin || checkinHour < 12)
                      )
                        return 4;
                      return 0;
                    })();
                    let cellBg;
                    if (leave && !checkin) cellBg = lc?.bg;
                    else if (isHalfLeave && checkin) cellBg = lc?.bg;
                    else if (isOvr) cellBg = '#fef3c7';
                    else if (hasMn || isHrManualCheckin) cellBg = '#dbeafe';
                    else if (td) cellBg = 'rgba(251,191,36,0.08)';
                    else if (hd || dow === 0) cellBg = weekendCellBg(dow, hd);
                    const leavePeriodLabel = leave
                      ? leave.period === 'FULL_DAY'
                        ? ' (เต็มวัน)'
                        : leave.period === 'MORNING'
                        ? ' (ครึ่งวันเช้า)'
                        : ' (ครึ่งวันบ่าย)'
                      : '';
                    const leaveTip = leave
                      ? [
                          `ลา${
                            LEAVE_ABBR[leave.leave_type] || leave.leave_type
                          }${leavePeriodLabel}`,
                          leave.reason ? `เหตุผล: ${leave.reason}` : '',
                          isHalfLeave && !checkin
                            ? `⚠ ไม่พบการลงเวลาช่วง${nonLeavePeriod}`
                            : '',
                          isHalfLeave &&
                          checkin &&
                          isCheckinInLeavePeriod === true
                            ? `⚠ ลงเวลาในช่วงลา (${checkin.checkInTime})`
                            : '',
                          isHalfLeave &&
                          checkin &&
                          isCheckinInLeavePeriod === false
                            ? `✓ ลงเวลาช่วง${nonLeavePeriod} (${checkin.checkInTime})`
                            : '',
                        ]
                          .filter(Boolean)
                          .join(' | ')
                      : '';
                    const weekendTone =
                      (hd || dow === 0) && !leave && !checkin && !hasMn && !isOvr && !td;
                    return (
                      <td
                        key={day}
                        style={dayTdBase({
                          background: cellBg,
                          borderRight: `1px solid ${
                            (leave && !checkin) || isHalfLeave
                              ? lc?.border
                              : BG.border
                          }`,
                          boxShadow: td
                            ? 'inset 0 0 0 1px #f59e0b'
                            : leave && !checkin
                            ? `inset 0 0 0 1px ${lc?.border}`
                            : isHalfLeave && checkin
                            ? `inset 3px 0 0 0 ${lc?.border}`
                            : undefined,
                        })}
                      >
                        {leave && !checkin ? (
                          /* ── วันลาอนุมัติ (ไม่มี checkin) ── */
                            <div
                              title={`${leaveTip} | คลิกขวาเพื่อดูรายละเอียด`}
                              style={dayCellInner({
                                cursor: 'context-menu',
                                borderBottom: `1px solid ${lc?.border}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                              })}
                              onContextMenu={(e) =>
                                openDv(
                                  e,
                                  null,
                                  emp.name,
                                  null,
                                  emp.salary_per_day,
                                  leave,
                                  leaveAutoH,
                                  day,
                                )
                              }
                            >
                              <span
                                style={{
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  color: lc?.text,
                                  letterSpacing: '-0.02em',
                                }}
                              >
                                {LEAVE_ABBR[leave.leave_type] || 'ลา'}
                              </span>
                              {leaveAutoH > 0 && (
                                <span
                                  style={{
                                    fontSize: '0.6rem',
                                    color: lc?.text,
                                    opacity: 0.9,
                                    marginLeft: 1,
                                  }}
                                >
                                  {leaveAutoH}h
                                </span>
                              )}
                              {isHalfLeave && leaveAutoH === 0 && (
                                <span
                                  style={{
                                    fontSize: '0.55rem',
                                    color: lc?.text,
                                    opacity: 0.7,
                                  }}
                                >
                                  ½
                                </span>
                              )}
                              {isHalfLeave && (
                                <span
                                  style={{
                                    fontSize: '0.6rem',
                                    color: '#dc2626',
                                    fontWeight: 800,
                                    lineHeight: 1,
                                  }}
                                >
                                  ✗
                                </span>
                              )}
                            </div>
                        ) : checkin ? (
                          /* ── มี checkin ปกติ (อาจมีลาด้วย แสดง badge เล็ก) ── */
                            <div
                              title={
                                leave
                                  ? `${
                                      isOvr ? ovrTip : 'รายละเอียดการลงเวลา'
                                    } | ลา${
                                      LEAVE_ABBR[leave.leave_type]
                                    }${leavePeriodLabel}`
                                  : isOvr
                                  ? ovrTip
                                  : 'คลิกเพื่อดูรายละเอียด / แก้ไขค่าแรง'
                              }
                              style={dayCellInner({
                                borderBottom: `1px solid ${BG.border}`,
                                cursor: 'pointer',
                                color: isOvr
                                  ? '#b45309'
                                  : weekendTone
                                  ? BG.weekendText
                                  : BG.text,
                                fontWeight: isOvr ? 700 : 500,
                                position: 'relative',
                              })}
                              onContextMenu={(e) =>
                                openDv(
                                  e,
                                  checkin,
                                  emp.name,
                                  getEntry(empId, day),
                                  emp.salary_per_day,
                                  leave,
                                  leaveAutoH,
                                )
                              }
                              onClick={() =>
                                openDetail(
                                  checkin,
                                  emp.name,
                                  emp.salary_per_day,
                                  !!checkin?.skip_late_deduct,
                                  empId,
                                )
                              }
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = isOvr
                                  ? 'rgba(245,158,11,0.15)'
                                  : 'rgba(99,102,241,0.08)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '';
                              }}
                            >
                              {nv != null || leaveAutoH > 0 ? (
                                <>
                                  {isHourForced && rawHoursBeforeForce != null && (
                                    <span
                                      style={{
                                        textDecoration: 'line-through',
                                        opacity: 0.5,
                                        fontSize: '0.7em',
                                        marginRight: 2,
                                      }}
                                    >
                                      {parseFloat(rawHoursBeforeForce.toFixed(2))}
                                    </span>
                                  )}
                                  {leaveAutoH > 0
                                    ? parseFloat(((nv != null ? nv * eff : 0) + leaveAutoH).toFixed(2))
                                    : parseFloat(nv.toFixed(2))}
                                  {leaveAutoH === 0 && eff > 1 && nv > 0 && (
                                    <span
                                      style={{
                                        fontSize: '0.6em',
                                        color: '#9ca3af',
                                        marginLeft: 1,
                                      }}
                                    >
                                      ({parseFloat((nv * eff).toFixed(2))})
                                    </span>
                                  )}
                                  {leaveAutoH > 0 && (
                                    <span
                                      style={{
                                        fontSize: '0.55em',
                                        color: lc?.text,
                                        marginLeft: 1,
                                      }}
                                    >
                                      +{parseFloat(leaveAutoH.toFixed(2))}
                                    </span>
                                  )}
                                  {isOvr && (
                                    <span
                                      style={{
                                        fontSize: '0.6em',
                                        marginLeft: 2,
                                      }}
                                    >
                                      ✎
                                    </span>
                                  )}

                                  {!isHourOverridden &&
                                    checkin?.deductionReasons?.includes(
                                      'no_checkout',
                                    ) && (
                                      <span
                                        style={{
                                          fontSize: '0.6em',
                                          color: '#ef4444',
                                          marginLeft: 2,
                                        }}
                                      >
                                        อ
                                      </span>
                                    )}
                                  {isLateArrival && (
                                    <span
                                      title={`เข้างาน ${checkin.checkInTime} (หลัง 08:00)`}
                                      style={{
                                        fontSize: '0.6em',
                                        color: '#f97316',
                                        fontWeight: 700,
                                        marginLeft: 2,
                                      }}
                                    >
                                      ส
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span style={{ visibility: 'hidden' }}>-</span>
                              )}
                              {leave && (
                                <span
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 1,
                                    fontSize: '0.5rem',
                                    color: lc?.text,
                                    fontWeight: 800,
                                    lineHeight: 1,
                                    background: lc?.bg,
                                    border: `1px solid ${lc?.border}`,
                                    padding: '1px 2px',
                                    borderRadius: 2,
                                  }}
                                >
                                  {isHalfLeave ? '½ลา' : 'ลา'}
                                </span>
                              )}
                            </div>
                        ) : (
                          /* ── ว่าง: เพิ่มชั่วโมงด้วยตนเอง ── */
                            <div
                              title={
                                hrMgmt
                                  ? hasMn
                                    ? `${mh} ชม. (กดเพื่อแก้ไข)`
                                    : 'กดเพื่อเพิ่มชั่วโมง'
                                  : ''
                              }
                              style={dayCellInner({
                                borderBottom: `1px solid ${BG.border}`,
                                cursor: hrMgmt ? 'pointer' : 'default',
                                color: weekendTone ? BG.weekendText : BG.text,
                              })}
                              onClick={
                                hrMgmt
                                  ? () =>
                                      openAddHours(
                                        empId,
                                        emp.name,
                                        day,
                                        emp.salary_per_day,
                                        emp.checkins?.find(
                                          (c) => (c.extra_day || 0) > 0,
                                        )?.extra_day || 0,
                                      )
                                  : undefined
                              }
                              onMouseEnter={(e) => {
                                if (hrMgmt)
                                  e.currentTarget.style.background =
                                    'rgba(37,99,235,0.08)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '';
                              }}
                            >
                              {hasMn ? (
                                <span
                                  style={{ fontWeight: 700, color: '#1d4ed8' }}
                                >
                                  {mh}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    color: '#d1d5db',
                                    fontSize: '0.85em',
                                  }}
                                >
                                  —
                                </span>
                              )}
                            </div>
                        )}
                          <div
                            title={
                              hrMgmt && !(leave && !checkin)
                                ? ov || manualOtH
                                  ? 'คลิกเพื่อแก้ไข OT | คลิกขวาเพื่อดูรายละเอียด'
                                  : 'คลิกเพื่อเพิ่ม OT'
                                : ov || manualOtH
                                ? 'คลิกขวาเพื่อดูรายละเอียด OT'
                                : ''
                            }
                            style={dayCellInner({
                              fontSize: '12px',
                              cursor:
                                hrMgmt && !(leave && !checkin)
                                  ? 'pointer'
                                  : 'default',
                              color: weekendTone ? BG.weekendText : undefined,
                            })}
                            onClick={
                              hrMgmt && !(leave && !checkin)
                                ? () => {
                                    if (!checkin) {
                                      setNoHoursWarnDay(day);
                                      return;
                                    }
                                    openAddOt(
                                      empId,
                                      emp.name,
                                      day,
                                      emp.salary_per_day,
                                      ov ? { hours: ov, rate: checkin?.otRequests?.rate } : null,
                                      {
                                        projectName: checkin?.project_name || null,
                                        projectNumber: checkin?.project_number || null,
                                      },
                                    );
                                  }
                                : undefined
                            }
                            onContextMenu={(e) =>
                              (checkin?.otRequests?.total_hours ||
                                manualOtH > 0) &&
                              openOtDv(
                                e,
                                checkin?.otRequests || null,
                                getEntry(empId, day),
                                emp.name,
                                checkin,
                                day,
                              )
                            }
                            onMouseEnter={(e) => {
                              if (hrMgmt && !(leave && !checkin))
                                e.currentTarget.style.background =
                                  'rgba(234,88,12,0.08)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '';
                            }}
                          >
                            {manualOtH > 0 ? (
                              <span
                                style={{ color: '#ea580c', fontWeight: 700 }}
                              >
                                {manualOtH}✎
                                <span
                                  style={{
                                    fontSize: '0.6em',
                                    color: '#9ca3af',
                                    marginLeft: 1,
                                  }}
                                >
                                  ({manualOtEffH.toFixed(1)})
                                </span>
                              </span>
                            ) : ov ? (
                              <span
                                style={{ color: '#ea580c', fontWeight: 500 }}
                              >
                                {ov}
                                <span
                                  style={{
                                    fontSize: '0.6em',
                                    color: '#9ca3af',
                                    marginLeft: 1,
                                  }}
                                >
                                  ({oeh.toFixed(1)})
                                </span>
                              </span>
                            ) : leave && !checkin && !isHalfLeave ? (
                              <span
                                style={{
                                  color: lc?.text,
                                  fontSize: '0.6rem',
                                  opacity: 0.8,
                                }}
                              >
                                {emp.emp_type === 'FULLTIME' ? '8h' : '1d'}
                              </span>
                            ) : leave && !checkin && isHalfLeave ? (
                              <span
                                style={{
                                  color: '#dc2626',
                                  fontSize: '0.6rem',
                                  fontWeight: 700,
                                }}
                              >
                                {emp.emp_type === 'FULLTIME' ? '4h ' : ''}
                                {nonLeavePeriod}✗
                              </span>
                            ) : isHalfLeave && checkin ? (
                              <span
                                style={{
                                  color: isCheckinInLeavePeriod
                                    ? '#dc2626'
                                    : '#16a34a',
                                  fontSize: '0.6rem',
                                  fontWeight: 700,
                                }}
                              >
                                {nonLeavePeriod}
                                {isCheckinInLeavePeriod ? '⚠' : '✓'}
                              </span>
                            ) : (
                              <span
                                style={{ color: '#e5e7eb', fontSize: '0.85em' }}
                              >
                                —
                              </span>
                            )}
                          </div>
                      </td>
                    );
                  })}

                  {/* ── RIGHT STICKY: Working ── */}
                  <td
                    style={{
                      ...td0({
                        borderRight: `1px solid ${BG.border}`,
                        ...(hrMgmt ? stickyRight(R.working) : stickyRight(0)),
                        fontWeight: 500,
                      }),
                      ...(hrMgmt ? {} : {}),
                    }}
                  >
                    <div
                      style={{
                        borderBottom: `1px solid ${BG.border}`,
                        padding: '4px 2px',
                        marginBottom: 4,
                        color: BG.text,
                        fontWeight: 500,
                      }}
                    >
                      {Number.isInteger(displayCheck)
                        ? displayCheck
                        : displayCheck.toFixed(1)}
                      {Math.abs(displayCheckEff - displayCheck) > 0.01 && (
                        <span
                          style={{
                            fontSize: '11px',
                            color: BG.textMuted,
                            marginLeft: 2,
                          }}
                        >
                          (
                          {Number.isInteger(displayCheckEff)
                            ? displayCheckEff
                            : displayCheckEff.toFixed(1)}
                          )
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#EA580C',
                        fontWeight: 500,
                        padding: '2px 0',
                      }}
                    >
                      {displayNetOtH > 0 || emp.totalOtHour ? (
                        <>
                          {displayNetOtH > 0
                            ? displayNetOtH.toFixed(1)
                            : emp.totalOtHour.toFixed(1)}
                          {Math.abs(displayNetOtEffH - displayNetOtH) >
                            0.01 && (
                            <span
                              style={{
                                fontSize: '11px',
                                color: BG.textMuted,
                                marginLeft: 2,
                              }}
                            >
                              ({displayNetOtEffH.toFixed(1)})
                            </span>
                          )}
                        </>
                      ) : (
                        '-'
                      )}
                    </div>
                  </td>

                  {/* ── RIGHT STICKY: financial columns ── */}
                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({
                          textAlign: 'right',
                          borderRight: `1px solid ${BG.border}`,
                          fontWeight: 500,
                          color: BG.text,
                          padding: '8px 8px',
                        }),
                        ...stickyRight(R.dayRate),
                      }}
                    >
                      <div style={{ fontWeight: 500, color: BG.text }}>
                        {fmt(emp.salary_per_day)}
                      </div>
                      <div
                        style={{
                          fontSize: '11px',
                          color: BG.subText,
                          marginTop: 2,
                        }}
                      >
                        ({fmt(emp.salary_per_day / 8, 2)}/ชม.)
                      </div>
                    </td>
                  )}

                  {hrMgmt && salaryMode !== 'work-time' && (
                    <td
                      style={{
                        ...td0({
                          textAlign: 'right',
                          fontWeight: 500,
                          padding: '8px 6px',
                        }),
                        ...stickyRight(R.salary),
                        background:
                          manualAmt > 0
                            ? 'rgba(37,99,235,0.05)'
                            : 'transparent',
                        color: manualAmt > 0 ? '#1D4ED8' : BG.text,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', gap: 2 }}>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 500 }}>
                            {fmt(displayAmt)}
                          </div>
                          <div style={{ fontSize: '11px', color: BG.subText, fontWeight: 400, marginTop: 2 }}>
                            {displayCheckEff > 0
                              ? `${Number.isInteger(displayCheckEff) ? displayCheckEff : displayCheckEff.toFixed(2)}×${fmt(emp.salary_per_day, 0)}`
                              : '—'}
                          </div>
                        </div>
                        {showFormulas && (
                          <button style={{ padding: 1, border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', flexShrink: 0, marginTop: 1, lineHeight: 1 }}
                            onClick={(ev) => setCalcPopover({ pos: { x: ev.clientX, y: ev.clientY }, title: 'สูตรค่าแรง', content: buildSalaryContent() })}>
                            <span style={{ fontSize: '0.65rem', lineHeight: 1 }}>ⓘ</span>
                          </button>
                        )}
                      </div>
                    </td>
                  )}

                  {hrMgmt && salaryMode !== 'late-deduct' && (
                    <td
                      style={{
                        ...td0({
                          textAlign: 'right',
                          fontWeight: 500,
                          padding: '8px 6px',
                        }),
                        ...stickyRight(R.actualSalary),
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', gap: 2 }}>
                        <div style={{ fontWeight: 500 }}>
                          {fmt(empSummaryMap[empId]?.actualSalaryAmt || 0)}
                        </div>
                        {showFormulas && (
                          <button style={{ padding: 1, border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', flexShrink: 0, marginTop: 1, lineHeight: 1 }}
                            onClick={(ev) => setCalcPopover({ pos: { x: ev.clientX, y: ev.clientY }, title: 'สูตรค่าแรง (ชม.จริง)', content: buildActualSalaryContent() })}>
                            <span style={{ fontSize: '0.65rem', lineHeight: 1 }}>ⓘ</span>
                          </button>
                        )}
                      </div>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({ padding: '3px' }),
                        ...stickyRight(R.ot),
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.revenue.overtime`}
                            control={control}
                            defaultValue={
                              (otTotalMap[empId] != null
                                ? Number(otTotalMap[empId]).toFixed(2)
                                : null) ??
                              (ff?.revenue?.overtime != null
                                ? Number(ff.revenue.overtime).toFixed(2)
                                : null) ??
                              (ff?.totalOtAmount != null
                                ? Number(ff.totalOtAmount).toFixed(2)
                                : null) ??
                              '0.00'
                            }
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                disabled
                                title="คำนวณอัตโนมัติจาก day cell — แก้ไขที่ day cell แทน"
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput('#15803d', true)}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-ot`,
                                    'overtime',
                                    'revenue.overtime',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'revenue.overtime',
                                    'overtime',
                                    `${empId}-ot`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span
                            style={{ fontSize: '0.75rem', color: '#64748b' }}
                          >
                            {fmt(otTotalMap[empId] ?? emp.totalOtAmount)}
                          </span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="overtime"
                          />
                        )}
                        {showFormulas && (
                          <button
                            style={{ padding: 1, border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
                            onClick={(ev) => setCalcPopover({ pos: { x: ev.clientX, y: ev.clientY }, title: 'สูตร OT', content: buildOtContent() })}
                          >
                            <span style={{ fontSize: '0.7rem', lineHeight: 1 }}>ⓘ</span>
                          </button>
                        )}
                      </div>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({ padding: '3px' }),
                        ...stickyRight(R.allow),
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.revenue.allowances`}
                            control={control}
                            defaultValue={
                              (ff?.revenue?.allowances != null
                                ? Number(ff.revenue.allowances).toFixed(2)
                                : null) ??
                              (ff?.total_extra_day != null
                                ? Number(ff.total_extra_day).toFixed(2)
                                : null) ??
                              '0.00'
                            }
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                disabled
                                title="คำนวณอัตโนมัติจาก day cell — แก้ไขที่ day cell แทน"
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput('#15803d', true)}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-allow`,
                                    'allowances',
                                    'revenue.allowances',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'revenue.allowances',
                                    'allowances',
                                    `${empId}-allow`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="allowances"
                          />
                        )}
                        {showFormulas && (
                          <button
                            style={{ padding: 1, border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
                            onClick={(ev) => setCalcPopover({ pos: { x: ev.clientX, y: ev.clientY }, title: 'สูตรเบี้ยเลี้ยง', content: buildAllowanceContent() })}
                          >
                            <span style={{ fontSize: '0.65rem', lineHeight: 1 }}>ⓘ</span>
                          </button>
                        )}
                      </div>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({
                          padding: '3px',
                          borderRight: `1px solid ${BG.border}`,
                        }),
                        ...stickyRight(R.bonus),
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.revenue.field_allowance`}
                            control={control}
                            defaultValue={Number(
                              faBreakdownMap[empId]?.total ??
                                ff?.revenue?.field_allowance ??
                                0,
                            ).toFixed(2)}
                            render={({ field: cf }) => {
                              // งวด locked: cf.value คือยอดที่บันทึกจริงตอนกดบันทึกเงินเดือน (ไม่ถูก
                              // sync ทับด้วยค่าคำนวณสดอีกแล้ว — ดู effect ที่ข้าม readOnly) ส่วน
                              // faBreakdownMap คำนวณสดตลอดตามโหมด fieldAllowanceMode ปัจจุบัน — ตอน
                              // locked ให้ "โชว์" ยอดสดนี้ตรงๆ แทนยอดบันทึกจริง จะได้เห็นผล toggle ทันที
                              // โดยไม่ต้องลบรายการเงินเดือนไปเช็ค (คอลัมน์รวม/สุทธิอื่นๆ ที่คำนวณจาก
                              // cf.value โดยตรงยังอิงยอดบันทึกจริงเหมือนเดิม ไม่กระทบ) ไฮไลต์แดง+tooltip
                              // บอกยอดบันทึกจริงไว้เทียบเมื่อไม่ตรงกัน
                              const faLive = Number(faBreakdownMap[empId]?.total ?? 0);
                              const faRecorded = Number(cf.value ?? 0);
                              const faMismatch = readOnly && Math.abs(faLive - faRecorded) > 0.01;
                              const displayValue = readOnly ? faLive.toFixed(2) : cf.value;
                              return (
                                <input
                                  {...cf}
                                  value={displayValue}
                                  disabled
                                  title={
                                    faMismatch
                                      ? `คำนวณสดตามโหมด "${fieldAllowanceMode === 'old' ? 'เก่า' : 'ใหม่'}" ได้ ฿${faLive.toFixed(2)} — บันทึกไว้จริงตอนกดบันทึกเงินเดือน ฿${faRecorded.toFixed(2)}`
                                      : 'คำนวณอัตโนมัติจาก day cell — แก้ไขที่ day cell แทน'
                                  }
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  style={mkInput(faMismatch ? '#b91c1c' : '#15803d', true)}
                                  onChange={(e) =>
                                    _handleChange(
                                      e,
                                      cf,
                                      `${empId}-fa`,
                                      'field_allowance',
                                      'revenue.field_allowance',
                                    )
                                  }
                                  onBlur={(e) =>
                                    _handleBlur(
                                      e,
                                      cf,
                                      'revenue.field_allowance',
                                      'field_allowance',
                                      `${empId}-fa`,
                                    )
                                  }
                                />
                              );
                            }}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="field_allowance"
                          />
                        )}
                        {showFormulas && fi !== undefined && (
                          <button
                            style={{ padding: 1, border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
                            onClick={(ev) => setCalcPopover({ pos: { x: ev.clientX, y: ev.clientY }, title: 'สูตรค่าออกสนาม', content: buildFieldAllowContent() })}
                          >
                            <span style={{ fontSize: '0.65rem', lineHeight: 1 }}>ⓘ</span>
                          </button>
                        )}
                      </div>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({
                          padding: '6px 4px',
                          borderRight: `1px solid ${BG.border}`,
                        }),
                        ...stickyRight(R.bonus),
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.revenue.bonus`}
                            control={control}
                            defaultValue={Number(
                              ff?.revenue?.bonus || 0,
                            ).toFixed(2)}
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                disabled={readOnly}
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput('#15803d', readOnly)}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-bon`,
                                    'bonus',
                                    'revenue.bonus',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'revenue.bonus',
                                    'bonus',
                                    `${empId}-bon`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="bonus"
                          />
                        )}
                      </div>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({
                          textAlign: 'right',
                          padding: '8px 6px',
                          borderRight: `1px solid ${BG.border}`,
                        }),
                        ...stickyRight(R.ssoIncome),
                      }}
                    >
                      {fi !== undefined ? (
                        <SsoIncomeCell control={control} fi={fi} fields={fields} />
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({ padding: '6px 4px' }),
                        ...stickyRight(R.tax),
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.expenses.tax`}
                            control={control}
                            defaultValue={Number(
                              ff?.expenses?.tax ??
                                (CalTax(
                                  ff?.salary_per_day * ff?.totalWork,
                                  ff?.tax_type,
                                ) || 0),
                            ).toFixed(2)}
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                disabled={readOnly}
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput('#b91c1c', readOnly)}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-tax`,
                                    'tax',
                                    'expenses.tax',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'expenses.tax',
                                    'tax',
                                    `${empId}-tax`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="tax"
                          />
                        )}
                      </div>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({ padding: '6px 4px' }),
                        ...stickyRight(R.sso),
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.expenses.sso`}
                            control={control}
                            defaultValue={
                              ff?.expenses?.sso ??
                              (CalSSO(
                                ff?.salary_per_day * ff?.totalWork,
                                ff?.sso_tax,
                                ff?.sso_paid,
                              ) ||
                                0)
                            }
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                disabled={readOnly}
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput('#b91c1c', readOnly)}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-sso`,
                                    'sso',
                                    'expenses.sso',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'expenses.sso',
                                    'sso',
                                    `${empId}-sso`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="sso"
                          />
                        )}
                        {showFormulas && fi !== undefined && (
                          <button
                            style={{ padding: 1, border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
                            onClick={(ev) => setCalcPopover({ pos: { x: ev.clientX, y: ev.clientY }, title: 'สูตรประกันสังคม', content: buildSsoContent() })}
                          >
                            <span style={{ fontSize: '0.65rem', lineHeight: 1 }}>ⓘ</span>
                          </button>
                        )}
                      </div>
                    </td>
                  )}

                  {hrMgmt && salaryMode !== 'work-time' && (
                    <td
                      style={{
                        ...td0({ padding: '6px 4px' }),
                        ...stickyRight(R.late),
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.expenses.late`}
                            control={control}
                            defaultValue={Number(
                              ff?.expenses?.late || 0,
                            ).toFixed(2)}
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                disabled={readOnly}
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput('#b91c1c', readOnly)}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-late`,
                                    'late',
                                    'expenses.late',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'expenses.late',
                                    'late',
                                    `${empId}-late`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="late"
                          />
                        )}
                        {showFormulas && (
                          <button
                            style={{ padding: 1, border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
                            onClick={(ev) => setCalcPopover({ pos: { x: ev.clientX, y: ev.clientY }, title: 'สูตรหักสาย', content: buildLateContent() })}
                          >
                            <span style={{ fontSize: '0.65rem', lineHeight: 1 }}>ⓘ</span>
                          </button>
                        )}
                      </div>
                    </td>
                  )}

                  {/* หัก CRL — read-only, มาจาก crl-summary (HrReportV2 ยัดลง expenses.crl) */}
                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({
                          textAlign: 'right',
                          padding: '8px 6px',
                        }),
                        ...stickyRight(R.crl),
                      }}
                    >
                      {fi !== undefined ? (
                        <CrlDeductCell control={control} fi={fi} fields={fields} />
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({
                          padding: '6px 4px',
                          borderRight: `1px solid ${BG.border}`,
                        }),
                        ...stickyRight(R.other),
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.expenses.other`}
                            control={control}
                            defaultValue={Number(
                              ff?.expenses?.other || 0,
                            ).toFixed(2)}
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                disabled={readOnly}
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput('#b91c1c', readOnly)}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-oth`,
                                    'other',
                                    'expenses.other',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'expenses.other',
                                    'other',
                                    `${empId}-oth`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="other"
                          />
                        )}
                      </div>
                    </td>
                  )}

                  {hrMgmt && salaryMode !== 'work-time' && (
                    <td
                      style={{
                        ...td0({ padding: '6px 4px' }),
                        ...stickyRight(R.net),
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
                        <div style={{ textAlign: 'right', fontWeight: 700 }}>
                          {fi !== undefined && (
                            <NetCell
                              control={control}
                              fi={fi}
                              fields={fields}
                              carryover={carryoverByEmpId[empId]}
                              carryoverOut={carryoverOutByEmpId[empId]}
                            />
                          )}
                        </div>
                        {showFormulas && fi !== undefined && (
                          <button style={{ padding: 1, border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
                            onClick={(ev) => setCalcPopover({ pos: { x: ev.clientX, y: ev.clientY }, title: 'สูตรคำนวณสุทธิ', content: buildNetContent() })}>
                            <span style={{ fontSize: '0.65rem', lineHeight: 1 }}>ⓘ</span>
                          </button>
                        )}
                      </div>
                    </td>
                  )}

                  {hrMgmt && salaryMode !== 'late-deduct' && (
                    <td
                      style={{
                        ...td0({ padding: '6px 4px' }),
                        ...stickyRight(R.actualNet),
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
                        <div style={{ textAlign: 'right', fontWeight: 700 }}>
                          {fi !== undefined && (
                            <ActualNetCell
                              control={control}
                              fi={fi}
                              fields={fields}
                              actualSalaryAmt={
                                empSummaryMap[empId]?.actualSalaryAmt || 0
                              }
                              carryover={carryoverByEmpId[empId]}
                              carryoverOut={carryoverOutByEmpId[empId]}
                            />
                          )}
                        </div>
                        {showFormulas && fi !== undefined && (
                          <button style={{ padding: 1, border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
                            onClick={(ev) => setCalcPopover({ pos: { x: ev.clientX, y: ev.clientY }, title: 'สูตรคำนวณที่ต้องจ่ายพนักงาน', content: buildActualNetContent() })}>
                            <span style={{ fontSize: '0.65rem', lineHeight: 1 }}>ⓘ</span>
                          </button>
                        )}
                      </div>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({ padding: '6px 4px' }),
                        ...stickyRight(R.companyCost),
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
                        <div style={{ textAlign: 'right' }}>
                          {fi !== undefined && (
                            <CompanyCostCell
                              control={control}
                              fi={fi}
                              fields={fields}
                              useActualSalary={salaryMode === 'work-time'}
                              actualSalaryAmt={empSummaryMap[empId]?.actualSalaryAmt || 0}
                            />
                          )}
                        </div>
                      </div>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({ padding: '3px' }),
                        ...stickyRight(R.note),
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.note`}
                            control={control}
                            defaultValue={ff?.note || ''}
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                disabled={readOnly}
                                type="text"
                                placeholder="หมายเหตุ..."
                                style={{
                                  width: '100%',
                                  border: `1px solid ${BG.border}`,
                                  borderRadius: 4,
                                  padding: '2px 6px',
                                  fontSize: '0.72rem',
                                  outline: 'none',
                                  color: '#475569',
                                  fontFamily: 'inherit',
                                }}
                                onChange={(e) => {
                                  cf.onChange(e);
                                  clearTimeout(
                                    adjDebounceRef.current[`${empId}-note`],
                                  );
                                  adjDebounceRef.current[
                                    `${empId}-note`
                                  ] = setTimeout(() => onAdjSave?.(empId), 1200);
                                }}
                                onBlur={(e) => {
                                  cf.onBlur(e);
                                  clearTimeout(
                                    adjDebounceRef.current[`${empId}-note`],
                                  );
                                  onAdjSave?.(empId);
                                }}
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        <CarryoverChip
                          incoming={carryoverByEmpId[empId]}
                          outgoing={carryoverOutByEmpId[empId]}
                          onClick={onOpenCarryover ? () => onOpenCarryover({ employee_id: empId, name: emp.name }) : undefined}
                        />
                      </div>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({ padding: '2px', borderRight: 'none' }),
                        ...stickyRight(R.manage),
                      }}
                    >
                      {fi !== undefined && remove && (
                        <button
                          title="ลบออกจากรายการ"
                          onClick={() => remove(fi)}
                          style={{ padding: 2, border: 'none', background: 'transparent', color: '#ef9a9a', cursor: 'pointer', lineHeight: 1 }}
                        >
                          <DeleteIcon style={{ fontSize: 15 }} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {spacerBottom > 0 && (
              <tr aria-hidden="true"><td colSpan={9999} style={{ height: spacerBottom, padding: 0, border: 'none' }} /></tr>
            )}
            {/* ── footer totals row ── */}
            <tr style={{ background: BG.footer }}>
              <td
                colSpan={3 + days.length}
                style={{
                  padding: '10px 12px',
                  textAlign: 'right',
                  fontWeight: 700,
                  fontSize: '14px',
                  color: BG.headerText,
                  background: BG.footer,
                  borderTop: `2px solid ${BG.border}`,
                  borderRight: `1px solid ${BG.border}`,
                }}
              >
                รวมทั้งหมด
              </td>
              <td
                style={{
                  padding: '10px 8px',
                  textAlign: 'center',
                  fontWeight: 700,
                  lineHeight: 1.5,
                  background: BG.footer,
                  borderTop: `2px solid ${BG.border}`,
                  borderRight: `1px solid ${BG.border}`,
                }}
              >
                <div
                  style={{
                    color: BG.headerText,
                    fontSize: '14px',
                    fontWeight: 700,
                  }}
                >
                  {totalsBase.work > 0
                    ? Number.isInteger(totalsBase.work)
                      ? totalsBase.work
                      : totalsBase.work.toFixed(1)
                    : '—'}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: '#EA580C',
                    fontWeight: 500,
                    marginTop: 2,
                  }}
                >
                  0.0
                </div>
              </td>
              {/* 1. Day Rate */}
              {hrMgmt && (
                <td
                  style={{
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                />
              )}
              {/* 2. Salary */}
              {hrMgmt && salaryMode !== 'work-time' && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    fontWeight: 700,
                    fontSize: '14px',
                    color: BG.headerText,
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  {fmt(totalsBase.salary)}
                </td>
              )}
              {/* 3. Actual Salary */}
              {hrMgmt && salaryMode !== 'late-deduct' && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    fontWeight: 700,
                    fontSize: '14px',
                    color: BG.headerText,
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  {fmt(totalsBase.actualSalary)}
                </td>
              )}
              {/* 4. OT */}
              {hrMgmt && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    fontWeight: 700,
                    fontSize: '14px',
                    color: BG.headerText,
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  {fmt(totalsBase.ot)}
                </td>
              )}
              {/* 5. Allowance (เบี้ยเลี้ยง) */}
              {hrMgmt && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  <TotalAllowanceCell
                    control={control}
                    fields={fields}
                    fieldIndexMap={fieldIndexMap}
                    timestamp={timestamp}
                  />
                </td>
              )}
              {/* 6. Field Allowance (ค่าออกสนาม) */}
              {hrMgmt && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  <TotalFieldAllowanceCell
                    control={control}
                    fields={fields}
                    fieldIndexMap={fieldIndexMap}
                    timestamp={timestamp}
                  />
                </td>
              )}
              {/* 7. Bonus (เงินได้อื่นๆ) */}
              {hrMgmt && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  <TotalBonusCell
                    control={control}
                    fields={fields}
                    fieldIndexMap={fieldIndexMap}
                    timestamp={timestamp}
                  />
                </td>
              )}
              {/* 8. SSO (income mirror) */}
              {hrMgmt && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  <TotalSsoIncomeCell
                    control={control}
                    fields={fields}
                    fieldIndexMap={fieldIndexMap}
                    timestamp={timestamp}
                  />
                </td>
              )}
              {/* 9. Tax (หักภาษี) */}
              {hrMgmt && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  <TotalTaxCell
                    control={control}
                    fields={fields}
                    fieldIndexMap={fieldIndexMap}
                    timestamp={timestamp}
                  />
                </td>
              )}
              {/* 10. SSO (deduct — หัก ประกันสังคม) */}
              {hrMgmt && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  <TotalSsoIncomeCell
                    control={control}
                    fields={fields}
                    fieldIndexMap={fieldIndexMap}
                    timestamp={timestamp}
                  />
                </td>
              )}
              {/* 10b. Late Deduct */}
              {hrMgmt && salaryMode !== 'work-time' && (
                <td
                  style={{
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                />
              )}
              {/* 10c. หัก CRL */}
              {hrMgmt && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  <TotalCrlDeductCell
                    control={control}
                    fields={fields}
                    fieldIndexMap={fieldIndexMap}
                    timestamp={timestamp}
                  />
                </td>
              )}
              {/* 11. Other Deduct (หักอื่นๆ) */}
              {hrMgmt && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  <TotalOtherDeductCell
                    control={control}
                    fields={fields}
                    fieldIndexMap={fieldIndexMap}
                    timestamp={timestamp}
                  />
                </td>
              )}
              {/* 12. Net */}
              {hrMgmt && salaryMode !== 'work-time' && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  <TotalNetCell
                    control={control}
                    fields={fields}
                    fieldIndexMap={fieldIndexMap}
                    timestamp={timestamp}
                    carryoverByEmpId={carryoverByEmpId}
                    carryoverOutByEmpId={carryoverOutByEmpId}
                  />
                </td>
              )}
              {/* 13. Actual Net */}
              {hrMgmt && salaryMode !== 'late-deduct' && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  <TotalActualNetCell
                    control={control}
                    fields={fields}
                    fieldIndexMap={fieldIndexMap}
                    timestamp={timestamp}
                    empSummaryMap={empSummaryMap}
                    carryoverByEmpId={carryoverByEmpId}
                    carryoverOutByEmpId={carryoverOutByEmpId}
                  />
                </td>
              )}
              {/* 13b. Company Cost */}
              {hrMgmt && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  <TotalCompanyCostCell
                    control={control}
                    fields={fields}
                    fieldIndexMap={fieldIndexMap}
                    timestamp={timestamp}
                    empSummaryMap={empSummaryMap}
                    useActualSalary={salaryMode === 'work-time'}
                  />
                </td>
              )}
              {/* 14. Note */}
              {hrMgmt && (
                <td
                  style={{
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                />
              )}
              {/* 15. Manage */}
              {hrMgmt && (
                <td
                  style={{
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: 'none',
                  }}
                />
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── LEGEND ── */}
      <div style={{ display: 'flex', gap: 10, padding: '8px 16px', flexWrap: 'wrap', alignItems: 'center', marginTop: 4, borderRadius: '0 0 8px 8px', background: '#F9FAFB', borderTop: `1px solid ${BG.border}` }}>
        <span style={{ color: BG.textMuted, fontWeight: 500, marginRight: 4, fontSize: 11 }}>สัญลักษณ์:</span>
        {[
          { color: '#fef9c3', border: '#d97706', label: 'แก้ไขค่าแรงแล้ว' },
          { color: '#dbeafe', border: '#3b82f6', label: 'เพิ่มชั่วโมงด้วยตนเอง' },
          { color: 'rgba(251,191,36,0.14)', border: '#f59e0b', label: 'วันนี้' },
          { color: BG.saturdayBg, border: '#FECACA', label: 'วันหยุด' },
          { color: BG.sundayBg, border: '#FCA5A5', label: 'อาทิตย์' },
          { color: LEAVE_COLOR.SICK.bg, border: LEAVE_COLOR.SICK.border, label: 'ลาป่วย' },
          { color: LEAVE_COLOR.PERSONAL.bg, border: LEAVE_COLOR.PERSONAL.border, label: 'ลากิจ' },
          { color: LEAVE_COLOR.VACATION.bg, border: LEAVE_COLOR.VACATION.border, label: 'ลาพักร้อน' },
        ].map(({ color, border, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: color, border: `1.5px solid ${border}`, flexShrink: 0 }} />
            <span style={{ color: BG.textMuted, fontSize: 11 }}>{label}</span>
          </div>
        ))}
        <span style={{ color: BG.textMuted, fontStyle: 'italic', fontSize: 11 }}>✎ = มีการแก้ไขค่าแรง</span>
      </div>

      {/* ── Calculation breakdown popup ── */}
      {calcPopover.pos && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1400 }} onClick={closeCp}>
          <div
            style={{
              position: 'fixed',
              top: calcPopover.pos.y,
              right: window.innerWidth - calcPopover.pos.x + 8,
              transform: 'translateY(-50%)',
              background: '#fff',
              borderRadius: 8,
              boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
              padding: '14px 16px',
              maxWidth: 380,
              zIndex: 1401,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: 8, fontSize: '0.875rem' }}>{calcPopover.title}</div>
            <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '0 0 8px' }} />
            {calcPopover.content}
          </div>
        </div>
      )}

      <OtDetailDialog
        open={otDvOpen}
        onClose={() => setOtDvOpen(false)}
        otDvData={otDvData}
        month={month}
        year={year}
      />

      {/* ── Employee Salary Report Modal ── */}
      {reportEmp && (
        <EmployeeSalaryReport
          open={!!reportEmp}
          onClose={() => setReportEmp(null)}
          emp={reportEmp.emp}
          month={month}
          year={year}
          manualWorkData={manualWorkData}
          leaveData={leaveData}
          payrollRow={
            reportEmp.fi !== undefined
              ? getValues
                ? getValues(`salaryData.${reportEmp.fi}`) ||
                  fields[reportEmp.fi]
                : fields[reportEmp.fi]
              : undefined
          }
          attendanceRules={attendanceRules}
          days={days}
          selectedOption={selectedOption}
          precomputedRows={reportEmp.dailyRows}
          salaryMode={salaryMode}
        />
      )}
    </div>
  );
}

export default React.memo(MergedPayrollTableV2);
